import type {
    SqliteDirtyPage,
    SqliteEngineDatabaseAdapter,
    SqliteEngineFactory,
    SqliteEngineHandle,
    SqliteEngineOpenConfig,
    SqliteMemoryManagerCompat,
} from '../sqliteEngine'
import { applySqlitePragmas, ensureSqliteWalMode } from '../sqlitePragmas'
import {
    DEFAULT_PAGE_SIZE,
    MAX_CONN,
    NUM_SLOTS,
    SAB1_DB_PAGE_COUNT_I32,
    Sab4Scalar,
    SlotMetaI32,
    sab1CtrlView,
    sab1SlotMetaView,
    sab2DataView,
    sab2SizeView,
    sab3LockView,
    sab3ShmView,
    sab4ScalarView,
} from './sab-layout'
import {
    createControlWorkerLifecycle,
    createAndInitializeSabSet,
    createClientWorkerConnState,
    type ControlWorkerRuntimeState,
    type SabSet,
} from './lifecycle'
import {
    maybeStartPushOrImport,
    runPush,
    runRemoteReconcile,
    type RunPushHooks,
    type RunReconcileHooks,
    type SyncEngineKickReason,
} from './sync-engine'
import {
    registerClientWorkerVfs,
    type Sqlite3WasmApi,
    type VfsRegistrationHandle,
} from './vfs-register'
import {
    beginHeadAdvancePreparationAndDrain,
    endHeadAdvancePreparation,
    flushPendingBaseAdvanceIfAny,
} from './commit-registration'
import { abortIfFatal, hasAnyOutstandingCommitPosts } from './control'
import { createSqlite3MtDatabaseAdapter } from './sqlite3mt-adapter'
export { createSqlite3MtDatabaseAdapter, type Sqlite3MtAdapterState } from './sqlite3mt-adapter'
import type { DurableLocalSnapshot, DurableLocalStore } from './durable'
import type {
    FullManifestOperations,
    SecureBlobRpcBridge,
} from './manifest-ops'
import {
    diffManifestTreesWithPageIndices,
    MANIFEST_V2_DEPTH,
    type Sqlite3mtManifestV2Node,
} from './public-manifest'
import { applyRemoteHeadIfNewer } from './remote-head'
import type { RemoteHeadUpdateMessage } from './control-runtime'
import { sdkConsole, type SdkLoggingOptions } from '../logger'
import {
    xWriteMainDb as writeSlotPage,
} from './slot'
import {
    captureDurableSnapshot,
    isTransientSyncError,
    persistSnapshotSerial,
    scheduleSyncRetry,
} from './snapshot-persistence'
import {
    appendWalFrames,
    ensureWalFrameAppendCapacity,
    updateWalIndex,
    updateWalIndexHdr,
    createWalFrameSabViews,
    type WalFrameSabViews,
} from './wal-frame-writer'
import { parseWalHeader, WAL_FRAME_HDR_SIZE, WAL_HDR_SIZE } from './wal-checksum'
import type { HashPageFn } from './wal-scan'
import {
    handleCheckpointWorkerRun,
    type CheckpointWorkerResult,
    type CheckpointDoneMessage,
    type FaultRequestMessage,
} from './worker-bridge'
import {
    sameWalSalt,
    SlotState,
    type Sqlite3MtDebugStagePayload,
    type AppendWalFramesMeta,
    type CheckpointMode,
    type ClientWorkerConnState,
    type FrameMax,
    type ImportTryResult,
    type ManifestOperations,
    type MergePlan,
    type PageHash,
    type Pgno,
    type Root,
    type WalCommitSyncMessage,
    type WalSnapshot,
} from './types'
import {
    awaitOutstandingCommitPostsToChangeOrEnterFatal,
    readWalSnapshotStable,
} from './checkpoint'
import { sab3ShmInt32View, tryReadWalSnapshotStable } from './shm-ops'
import {
    buildImportDeltaFromPlan,
    enterUncertainAppendingFatal,
    INTERNAL_PENDING_BASE_ADVANCE_REENTRY,
    reconcileOrphanProbeState,
    reconcileTailCanStillIntegrateSoon,
    sameReconcileOrphanProbeState,
    tryImportPreparedIntentAsSingleWalCommit,
} from './reconcile'
import {
    buildMergePlan,
    buildResidualOverlayAgainstRemote,
    ensureMetadataCarrierFrameIfNeeded,
} from './merge-plan'
import type { SdkObservers } from '../observers'
import {
    emitSecureBlobComponentProgress,
    readSecureBlobRuntimeEnv,
} from '../componentRuntimeProgress'

const REMOTE_REFRESH_INFLIGHT_STALE_AFTER_MS = 5_000
const LOCAL_BACKGROUND_SYNC_DEBOUNCE_MS = 5_000
const LOCAL_BACKGROUND_SYNC_REASONS = new Set<SyncEngineKickReason>([
    'local-commit',
    'checkpoint-done',
    'external-client-idle',
])
const LOCAL_MAINTENANCE_REASONS = new Set<SyncEngineKickReason>([
    'adapter-flush-local',
])

export interface Sqlite3Oo1Database {
    readonly pointer: number
    exec(sql: string | { sql: string; bind?: unknown[]; rowMode?: string; callback?: (row: unknown) => void }): void
    close(): void
}

export interface Sqlite3WasmRuntimeApi extends Sqlite3WasmApi {
    oo1: {
        DB: new (options: { filename: string; flags: string; vfs?: string }) => Sqlite3Oo1Database
    }
}

export interface Sqlite3MtHandleHooks {
    getDirtyPages(): SqliteDirtyPage[]
    resetDirtyTracking(): void
    exportFullDb(): Uint8Array
    importFullDb(bytes: Uint8Array): void
    exportDirtyPages(): Map<number, Uint8Array>
    importMergedPages(pages: Map<number, Uint8Array>): void
    appendWalFramesForSync(pages: Map<number, Uint8Array>): void
    readPages(pgnos: number[]): Promise<Map<number, Uint8Array>>
}

export type Sqlite3MtConnectionRole = 'runtime-owner' | 'client' | 'checkpoint'

export interface Sqlite3MtHandleContext {
    readonly role: Sqlite3MtConnectionRole
    readonly config: SqliteEngineOpenConfig
    readonly connState: ClientWorkerConnState
    readonly sabs: SabSet
    /** Open oo1 DB handle for this connection. */
    readonly db: Sqlite3Oo1Database
}

export interface Sqlite3MtRuntimeBridge {
    readonly runtimeEpoch: bigint
    readonly databaseAdapter?: SqliteEngineDatabaseAdapter
    readonly dbName: string
    hashPage(pageData: Uint8Array): PageHash
    createHandleHooks(context: Sqlite3MtHandleContext): Partial<Sqlite3MtHandleHooks> | void
    postFaultMessage(conn: ClientWorkerConnState, msg: FaultRequestMessage): void
    postCommitSyncMessage(
        conn: ClientWorkerConnState,
        msg: WalCommitSyncMessage,
        transfer: ArrayBuffer[]
    ): void
    enterFatalIoMode(conn: ClientWorkerConnState): void
    handleExternalMessage?(
        message: Sqlite3MtExternalMessage
    ): void
    enterExternalClientIoFatal?(connId: number): void
    enterExternalClientRequest?(connId: number, runtimeEpoch: bigint): void
    leaveExternalClientRequest?(connId: number, runtimeEpoch: bigint): void
    releaseExternalClientAttachment?(connId: number, runtimeEpoch: bigint): void
    runWalCheckpoint?: (mode: CheckpointMode) => CheckpointWorkerResult
    dispose(): void | Promise<void>
}

export interface Sqlite3MtClientAttachment {
    readonly connId: number
    readonly runtimeEpoch: bigint
    readonly dbName: string
    readonly pageSize: number
    readonly bootstrapStoragePragmas?: boolean
    readonly logging?: SdkLoggingOptions
    readonly sab1: SharedArrayBuffer
    readonly sab2: SharedArrayBuffer
    readonly sab3: SharedArrayBuffer
    readonly sab4: SharedArrayBuffer
}

type Sqlite3MtExternalMessage =
    | FaultRequestMessage
    | WalCommitSyncMessage
    | CheckpointDoneMessage
    | RemoteHeadUpdateMessage

interface Sqlite3MtAttachmentCapableFactory extends SqliteEngineFactory {
    reserveClientAttachment(): Sqlite3MtClientAttachment
    releaseClientAttachment(connId: number): void
    dispatchExternalMessage(message: Sqlite3MtExternalMessage): void
    enterExternalClientIoFatal(connId: number): void
    enterExternalClientRequest(connId: number, runtimeEpoch: bigint): void
    leaveExternalClientRequest(connId: number, runtimeEpoch: bigint): void
}

export interface CreateSqlite3MtRuntimeArgs {
    readonly config: SqliteEngineOpenConfig
    readonly sabs: SabSet
}

export interface Sqlite3MtEngineFactoryDeps {
    loadSqlite3(): Promise<Sqlite3WasmRuntimeApi>
    createRuntime(args: CreateSqlite3MtRuntimeArgs): Promise<Sqlite3MtRuntimeBridge> | Sqlite3MtRuntimeBridge
    virtualRuntimeOwner?: boolean
    initialWalSize?: number
    maxWalSize?: number
    logging?: SdkLoggingOptions
}

interface Sqlite3MtBootstrapCallbacks {
    readonly onPageMiss: SqliteEngineOpenConfig['onPageMiss']
    readonly onCommit: SqliteEngineOpenConfig['onCommit']
}

export type Sqlite3MtBootstrapOpenMode =
    | ({
        readonly kind: 'initial-db'
        readonly initialDb: Uint8Array
    } & Sqlite3MtBootstrapCallbacks)
    | ({
        readonly kind: 'seed-pages'
        readonly seedPages: Map<number, Uint8Array>
        readonly pageCount: number
    } & Sqlite3MtBootstrapCallbacks)
    | ({
        readonly kind: 'empty'
    } & Sqlite3MtBootstrapCallbacks)

/**
 * Normalize the runtime-owner bootstrap contract into explicit modes.
 *
 * This mirrors `openSqliteDb()` precedence so the sqlite3mt replacement path
 * can implement the same bootstrap semantics without re-deriving them ad hoc:
 * full `initialDb` import wins, otherwise lazily seeded pages, otherwise empty.
 */
export function resolveSqlite3MtBootstrapOpenMode(
    config: SqliteEngineOpenConfig
): Sqlite3MtBootstrapOpenMode {
    if (config.initialDb != null && config.initialDb.length > 0) {
        return {
            kind: 'initial-db',
            initialDb: config.initialDb,
            onPageMiss: config.onPageMiss,
            onCommit: config.onCommit,
        }
    }

    if (config.seedPages != null && config.seedPages.size > 0) {
        return {
            kind: 'seed-pages',
            seedPages: new Map(config.seedPages),
            pageCount: config.pageCount ?? Math.max(...config.seedPages.keys()),
            onPageMiss: config.onPageMiss,
            onCommit: config.onCommit,
        }
    }

    return {
        kind: 'empty',
        onPageMiss: config.onPageMiss,
        onCommit: config.onCommit,
    }
}

function detectSqlitePageSizeFromInitialDb(initialDb: Uint8Array | undefined): number | null {
    if (initialDb == null || initialDb.length < 18) {
        return null
    }

    let pageSize = (initialDb[16]! << 8) | initialDb[17]!
    if (pageSize === 1) {
        pageSize = 65536
    }

    return pageSize >= 512 ? pageSize : null
}

function resolveSqlite3MtOpenPageSize(config: SqliteEngineOpenConfig): number {
    return config.pageSize
        ?? detectSqlitePageSizeFromInitialDb(config.initialDb)
        ?? DEFAULT_PAGE_SIZE
}

function initialDbPageCount(initialDb: Uint8Array, pageSize: number): number {
    return Math.ceil(initialDb.length / pageSize)
}

function takeSeedPagesFromInitialDb(
    initialDb: Uint8Array,
    pageSize: number
): Map<number, Uint8Array> {
    const seedPages = new Map<number, Uint8Array>()
    const pageCount = initialDbPageCount(initialDb, pageSize)

    for (let pageIndex = 0; pageIndex < Math.min(pageCount, NUM_SLOTS); pageIndex += 1) {
        const offset = pageIndex * pageSize
        seedPages.set(pageIndex + 1, initialDb.slice(offset, offset + pageSize))
    }

    return seedPages
}

function primeBootstrapPagesIntoSab(
    sabs: Pick<SabSet, 'sab1' | 'pageSize'>,
    seedPages: ReadonlyMap<number, Uint8Array>,
    pageCount: number
): void {
    const ctrl = sab1CtrlView(sabs.sab1)
    Atomics.store(ctrl, SAB1_DB_PAGE_COUNT_I32, pageCount)

    for (const [pgno, pageBytes] of seedPages) {
        writeSlotPage(pgno, pageBytes, sabs.sab1, sabs.pageSize)
    }
}

export interface Sqlite3MtLifecycleRuntimeBridgeDeps {
    durable: DurableLocalStore
    manifest:
        & Pick<FullManifestOperations, 'getPageHashFromConcreteRoot'>
        & Partial<Pick<
            FullManifestOperations,
            'normalizeSparseOverlay' | 'materializeRoot' | 'collectUploadSet'
            | 'diffConcreteRoots' | 'getPageBytesByHash'
        >>
    rpc:
        & Pick<SecureBlobRpcBridge, 'rpcGetBlob' | 'rpcGetHeadVersioned' | 'rpcGetManifestNode'>
        & Partial<Pick<
            SecureBlobRpcBridge,
            'rpcGetBlobs' | 'rpcGetManifestNodes' | 'rpcPublishIfCurrent' | 'rpcPutMissingBlobs' | 'rpcPutMissingManifestNodes'
        >>
    hashPage: HashPageFn
    createHandleHooks?(context: Sqlite3MtHandleContext): Partial<Sqlite3MtHandleHooks> | void
    dbName?: string
    databaseAdapter?: SqliteEngineDatabaseAdapter
    loadWalBytesForBootstrap?(): Uint8Array | Promise<Uint8Array>
    primeLiveWalViewForBootstrap?(): Promise<void>
    tryReadWalSnapshotStableOrNull?(): import('./types').WalSnapshot | null
    yieldOneTaskTurnForPendingCommitMessages?(): Promise<void>
    onShutdown?(): void | Promise<void>
    onSnapshotPersistError?(error: unknown): void
    refreshRemoteHeadAfterBootstrap?(): void
    maybeCompactDbBaseRoot?(): void
    readLiveWalSnapshot?(): import('./types').WalSnapshot | null
    maxPassiveCheckpointNoProgress?: number
    nowMs?(): number
    setTimeoutFn?: import('./snapshot-persistence').TimeoutScheduler
    kickSyncEngine?(reason: SyncEngineKickReason): void
    runWalCheckpoint?(mode: CheckpointMode): CheckpointWorkerResult
    observers?: SdkObservers
    faultReadAheadPages?: number
}

interface RuntimeContext {
    readonly runtimeId: number
    readonly sqlite3: Sqlite3WasmRuntimeApi
    readonly sabs: SabSet
    readonly runtime: Sqlite3MtRuntimeBridge
    readonly config: SqliteEngineOpenConfig
    readonly dbName: string
    memoryManager: SqliteMemoryManagerCompat
    readonly activeConnIds: Set<number>
    readonly registrations: Map<number, VfsRegistrationHandle>
    handleCount: number
    checkpointHandle: SqliteEngineHandle | null
    runtimeOwnerHooks: Sqlite3MtHandleHooks | null
    disposed: boolean
}

let nextRuntimeId = 1

const SQLITE3MT_RUNTIME_DISPATCH_FAILED = 'SQLITE3MT_RUNTIME_DISPATCH_FAILED'
const SQLITE3MT_CLIENT_IO_FATAL = 'SQLITE3MT_CLIENT_IO_FATAL'
const SQLITE3MT_PUSH_BLOB_MISSING = 'SQLITE3MT_PUSH_BLOB_MISSING'

/**
 * Resolves a push-overlay page blob from staged CAS -> durable local store ->
 * remote RPC, caching the recovered bytes back into staged CAS. Returns null
 * only when the blob is genuinely absent everywhere (genuine data loss).
 *
 * Exported so the push-path page resolution (which must self-heal a locally
 * missing page blob as long as it still exists remotely) is unit-testable
 * without spinning up the full sqlite3mt runtime bridge. Mirrors the lookup
 * order of the reconcile manifest view's getPageBytesByHash.
 */
export async function resolveSqlite3MtPushPageBlob(
    hash: PageHash,
    deps: {
        stagedCas: Map<PageHash, Uint8Array>
        getBlob: (hash: PageHash) => Promise<Uint8Array | null>
        rpcGetBlob: (hash: PageHash) => Promise<Uint8Array | null>
    },
): Promise<Uint8Array | null> {
    let bytes = deps.stagedCas.get(hash) ?? null
    if (bytes == null) bytes = await deps.getBlob(hash)
    if (bytes == null) {
        const remote = await deps.rpcGetBlob(hash)
        if (remote != null) {
            deps.stagedCas.set(hash, remote)
            bytes = remote
        }
    }
    return bytes
}
const SQLITE_MAIN_DB_NAME = new TextEncoder().encode('main\0')
const DEFAULT_SQLITE3MT_OPEN_TIMEOUT_MS = 300_000
const MAX_SQLITE3MT_OPEN_TIMEOUT_MS = 900_000

type PushCapableManifestOps = Pick<
    FullManifestOperations,
    'normalizeSparseOverlay' | 'materializeRoot' | 'collectUploadSet'
>

type PushCapableRpcBridge = Pick<
    SecureBlobRpcBridge,
    'rpcPublishIfCurrent' | 'rpcPutMissingBlobs' | 'rpcPutMissingManifestNodes'
>

function readSqlite3MtOpenTimeoutMs(): number {
    const value = readSecureBlobRuntimeEnv('SECUREBLOB_SQLITE3MT_OPEN_TIMEOUT_MS')
    if (value == null || value.trim().length === 0) {
        return DEFAULT_SQLITE3MT_OPEN_TIMEOUT_MS
    }

    const parsed = Number(value)
    if (Number.isFinite(parsed) && parsed >= 1 && parsed <= MAX_SQLITE3MT_OPEN_TIMEOUT_MS) {
        return Math.trunc(parsed)
    }

    sdkConsole.warn('[sqlite3mt:engine] ignoring invalid open timeout override', {
        value,
        fallback: DEFAULT_SQLITE3MT_OPEN_TIMEOUT_MS,
    })
    return DEFAULT_SQLITE3MT_OPEN_TIMEOUT_MS
}

function withSqlite3MtOpenTimeout<T>(
    promise: Promise<T>,
    label: string,
    timeoutMs = readSqlite3MtOpenTimeoutMs(),
): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const timeout = setTimeout(() => {
            reject(new Error(`sqlite3mt open timed out after ${timeoutMs}ms: ${label}`))
        }, timeoutMs)

        promise.then(
            value => {
                clearTimeout(timeout)
                resolve(value)
            },
            error => {
                clearTimeout(timeout)
                reject(error)
            },
        )
    })
}

function defaultDbName(workspace: string, runtimeId: number): string {
    const sanitized = workspace.replace(/[^A-Za-z0-9_.-]+/g, '_').replace(/^_+|_+$/g, '')
    return `${sanitized || 'sqlite3mt'}_${runtimeId}`
}

function buildDbFilename(dbName: string): string {
    return dbName
}

function noopKickSyncEngine(_reason: SyncEngineKickReason): void {}

type Sqlite3CheckpointCapableCapi = Sqlite3WasmRuntimeApi['capi'] & {
    sqlite3_wal_checkpoint_v2?: (
        dbPtr: number,
        dbNamePtr: number,
        mode: number,
        logFramesPtr: number,
        ckptFramesPtr: number,
    ) => number
    SQLITE_CHECKPOINT_PASSIVE?: number
    SQLITE_CHECKPOINT_RESTART?: number
    SQLITE_CHECKPOINT_TRUNCATE?: number
}

function formatSqlite3MtUnknownError(error: unknown): string {
    if (error instanceof Error) {
        return error.message
    }
    if (typeof error === 'string') {
        return error
    }
    try {
        return JSON.stringify(error)
    } catch {
        return String(error)
    }
}

async function yieldOneTaskTurn(): Promise<void> {
    await new Promise<void>(resolve => {
        setTimeout(resolve, 0)
    })
}

function cloneWalSnapshot(snapshot: WalSnapshot | null): WalSnapshot | null {
    return snapshot == null ? null : { ...snapshot }
}

function isZeroWalSnapshot(snapshot: WalSnapshot): boolean {
    return snapshot.salt0 === 0
        && snapshot.salt1 === 0
        && snapshot.iChange === 0
        && snapshot.mxFrame === 0
        && snapshot.nBackfill === 0
}

function hasVisibleWalFrames(snapshot: WalSnapshot): boolean {
    return snapshot.mxFrame > 0 || snapshot.nBackfill > 0
}

function normalizeObservedWalSnapshot(snapshot: WalSnapshot | null | undefined): WalSnapshot | null {
    if (snapshot == null || isZeroWalSnapshot(snapshot) || !hasVisibleWalFrames(snapshot)) {
        return null
    }

    return snapshot
}

function mergeObservedWalSnapshotsSameSalt(snapshots: readonly WalSnapshot[]): WalSnapshot {
    const [first, ...rest] = snapshots
    if (first == null) {
        throw new Error('mergeObservedWalSnapshotsSameSalt requires at least one snapshot')
    }

    return rest.reduce<WalSnapshot>((merged, snapshot) => ({
        salt0: merged.salt0,
        salt1: merged.salt1,
        iChange: Math.max(merged.iChange, snapshot.iChange),
        mxFrame: Math.max(merged.mxFrame, snapshot.mxFrame),
        nBackfill: Math.min(
            Math.max(merged.nBackfill, snapshot.nBackfill),
            Math.max(merged.mxFrame, snapshot.mxFrame),
        ),
    }), { ...first })
}

export function selectPreferredLiveWalSnapshot(
    snapshots: ReadonlyArray<WalSnapshot | null | undefined>,
    preferredCycle?: Pick<WalSnapshot, 'salt0' | 'salt1'> | null,
    authoritativeCycle?: Pick<WalSnapshot, 'salt0' | 'salt1'> | null,
): WalSnapshot | null {
    const normalized = snapshots
        .map(normalizeObservedWalSnapshot)
        .filter((snapshot): snapshot is WalSnapshot => snapshot != null)

    if (normalized.length === 0) {
        return null
    }

    const chooseFrom = (
        candidates: readonly WalSnapshot[],
    ): WalSnapshot => {
        const groups = new Map<string, WalSnapshot[]>()
        for (const snapshot of candidates) {
            const key = `${snapshot.salt0}:${snapshot.salt1}`
            const existing = groups.get(key)
            if (existing != null) {
                existing.push(snapshot)
            } else {
                groups.set(key, [snapshot])
            }
        }

        let best: WalSnapshot | null = null
        for (const group of groups.values()) {
            const merged = mergeObservedWalSnapshotsSameSalt(group)
            if (
                best == null
                || merged.mxFrame > best.mxFrame
                || (merged.mxFrame === best.mxFrame && merged.nBackfill > best.nBackfill)
                || (
                    merged.mxFrame === best.mxFrame
                    && merged.nBackfill === best.nBackfill
                    && (
                        merged.salt0 > best.salt0
                        || (merged.salt0 === best.salt0 && merged.salt1 > best.salt1)
                    )
                )
            ) {
                best = merged
            }
        }

        if (best == null) {
            throw new Error('chooseFrom requires at least one merged snapshot')
        }

        return best
    }

    const chooseMatchingCycle = (
        cycle: Pick<WalSnapshot, 'salt0' | 'salt1'> | null | undefined,
    ): WalSnapshot | null => {
        if (cycle == null) {
            return null
        }

        const matching = normalized.filter(snapshot =>
            sameWalSalt(
                snapshot.salt0,
                snapshot.salt1,
                cycle.salt0,
                cycle.salt1,
            )
        )
        if (matching.length > 0) {
            return chooseFrom(matching)
        }

        return null
    }

    const authoritative = chooseMatchingCycle(authoritativeCycle)
    if (authoritative != null) {
        return authoritative
    }

    const preferred = chooseMatchingCycle(preferredCycle)
    if (preferred != null) {
        return preferred
    }

    return chooseFrom(normalized)
}

export function pruneStagedSqlite3MtCasToSnapshotRequirements(
    stagedCas: Map<PageHash, Uint8Array>,
    stagedManifestNodes: Map<string, Uint8Array>,
    snapshot: Pick<DurableLocalSnapshot, 'requiredBlobHashes' | 'requiredManifestNodeHashes'>,
): void {
    for (const hash of stagedCas.keys()) {
        if (!snapshot.requiredBlobHashes.has(hash)) {
            stagedCas.delete(hash)
        }
    }
    for (const hash of stagedManifestNodes.keys()) {
        if (!snapshot.requiredManifestNodeHashes.has(hash)) {
            stagedManifestNodes.delete(hash)
        }
    }
}

function deriveWalSnapshotFromWalBytes(
    sabs: Pick<SabSet, 'sab2'>
): WalSnapshot | null {
    const walSize = Atomics.load(sab2SizeView(sabs.sab2), 0)
    if (walSize < WAL_HDR_SIZE) {
        return null
    }

    const walHeader = parseWalHeader(sab2DataView(sabs.sab2).subarray(0, walSize))
    if (walHeader == null) {
        return null
    }

    const frameSize = walHeader.pageSize + WAL_FRAME_HDR_SIZE
    const frameBytes = walSize - WAL_HDR_SIZE
    if (frameSize <= 0 || frameBytes < 0 || frameBytes % frameSize !== 0) {
        return null
    }

    if (frameBytes === 0) {
        return null
    }

    return {
        salt0: walHeader.salt1,
        salt1: walHeader.salt2,
        iChange: 0,
        mxFrame: frameBytes / frameSize,
        nBackfill: 0,
    }
}

export function tryReadInitializedSharedWalSnapshot(
    sabs: Pick<SabSet, 'sab2' | 'sab3'>
): WalSnapshot | null {
    const shared = tryReadWalSnapshotStable(sab3ShmInt32View(sabs.sab3), { timeoutMs: 0 })
    if (shared != null && !isZeroWalSnapshot(shared) && hasVisibleWalFrames(shared)) {
        return shared
    }

    return deriveWalSnapshotFromWalBytes(sabs)
}

function hasInternalPushDriverDeps(
    deps: Sqlite3MtLifecycleRuntimeBridgeDeps
): deps is Sqlite3MtLifecycleRuntimeBridgeDeps & {
    manifest: Pick<FullManifestOperations, 'getPageHashFromConcreteRoot'> & PushCapableManifestOps
    rpc: Pick<SecureBlobRpcBridge, 'rpcGetBlob' | 'rpcGetHeadVersioned'> & PushCapableRpcBridge
} {
    return typeof deps.manifest.normalizeSparseOverlay === 'function'
        && typeof deps.manifest.materializeRoot === 'function'
        && typeof deps.manifest.collectUploadSet === 'function'
        && typeof deps.rpc.rpcPublishIfCurrent === 'function'
        && typeof deps.rpc.rpcPutMissingBlobs === 'function'
        && typeof deps.rpc.rpcPutMissingManifestNodes === 'function'
}

type ReconcileCapableManifestOps = Pick<
    FullManifestOperations,
    'getPageHashFromConcreteRoot' | 'diffConcreteRoots' | 'getPageBytesByHash'
>

interface ReconcileBlobLookupState {
    stagedCas: Map<PageHash, Uint8Array>
    stagedManifestNodes: Map<string, Uint8Array>
}

type RpcDebugStageCapable = {
    getDebugStage?: () => string | null
    getDebugDetails?: () => unknown
}

function parseStagedManifestNode(bytes: Uint8Array): Sqlite3mtManifestV2Node | null {
    try {
        const parsed = JSON.parse(new TextDecoder().decode(bytes))
        if (parsed && parsed.type === 'sqlite3mt-manifest-v2') {
            return parsed as Sqlite3mtManifestV2Node
        }
        return null
    } catch {
        return null
    }
}

function assertConcreteManifestRoot(root: Root): string {
    if (root.meta.manifestRootId) {
        return root.meta.manifestRootId
    }
    if (root.meta.pageCount === 0) {
        throw new Error('EMPTY_MANIFEST_ROOT')
    }
    throw new Error(`INVALID_MANIFEST_ROOT: ${root.rootId}`)
}

export function createReconcileManifestView(
    state: ReconcileBlobLookupState,
    manifest: ManifestOperations,
    durable: Pick<DurableLocalStore, 'getBlob' | 'getManifestNode'>,
    rpc: Pick<SecureBlobRpcBridge, 'rpcGetBlob' | 'rpcGetManifestNode'> & Partial<Pick<SecureBlobRpcBridge, 'rpcGetManifestNodes'>>,
): ManifestOperations {
    const emitBrowserDebugStage = (payload: Sqlite3MtDebugStagePayload): void => {
        const globalScope = globalThis as typeof globalThis & {
            __sqlite3mtEmitDebugStage?: (payload: Sqlite3MtDebugStagePayload) => void
        }
        globalScope.__sqlite3mtEmitDebugStage?.(payload)
    }
    const setStage = (stage: string): void => {
        const debugState = state as ReconcileBlobLookupState & {
            __debugReconcileStage?: string
            __debugStageTrace?: Array<{
                kind: 'reconcile-manifest'
                stage: string
                remoteVersion: string
                localVersion: string
            }>
            remoteHead?: { version: bigint }
            workspaceBaseHead?: { version: bigint }
            workspaceOverlay?: { size: number }
        }
        const remoteVersion = debugState.remoteHead?.version?.toString() ?? 'n/a'
        const localVersion = debugState.workspaceBaseHead?.version?.toString() ?? 'n/a'
        const overlaySize = debugState.workspaceOverlay?.size ?? 0
        debugState.__debugReconcileStage = stage
        const trace = debugState.__debugStageTrace ?? []
        trace.push({
            kind: 'reconcile-manifest',
            stage,
            remoteVersion,
            localVersion,
        })
        if (trace.length > 32) {
            trace.splice(0, trace.length - 32)
        }
        debugState.__debugStageTrace = trace
        sdkConsole.trace('[sqlite3mt:engine] stage', {
            kind: 'reconcile-manifest',
            stage,
            remoteVersion,
            localVersion,
            overlaySize,
        })
        emitBrowserDebugStage({
            kind: 'reconcile-manifest',
            stage,
            remoteVersion,
            localVersion,
            overlaySize,
        })
    }
    const concreteRootPageHashCache = new Map<string, PageHash | null>()
    const MANIFEST_CHILD_PREFETCH_RADIUS = 4

    const loadManifestNodeBytesBatch = async (
        hashes: string[],
        stagePrefix: string,
    ): Promise<Map<string, Uint8Array>> => {
        const result = new Map<string, Uint8Array>()
        const uniqueHashes = Array.from(new Set(hashes.filter(Boolean)))
        const durableMisses: string[] = []
        for (const hash of uniqueHashes) {
            const staged = state.stagedManifestNodes.get(hash) ?? null
            if (staged != null) {
                result.set(hash, staged)
                continue
            }
            setStage(`${stagePrefix}:${hash}:durable`)
            const durableBytes = await durable.getManifestNode(hash)
            if (durableBytes != null) {
                state.stagedManifestNodes.set(hash, durableBytes)
                result.set(hash, durableBytes)
            } else {
                durableMisses.push(hash)
            }
        }
        if (durableMisses.length === 0) return result

        setStage(`${stagePrefix}:${durableMisses.length}:remote-batch`)
        const remoteBytes = rpc.rpcGetManifestNodes
            ? await rpc.rpcGetManifestNodes(durableMisses)
            : new Map(await Promise.all(durableMisses.map(async hash => [
                hash,
                await rpc.rpcGetManifestNode(hash),
            ] as const)).then(entries => entries.filter((entry): entry is [string, Uint8Array] => entry[1] != null)))
        for (const [hash, bytes] of remoteBytes) {
            state.stagedManifestNodes.set(hash, bytes)
            result.set(hash, bytes)
        }
        return result
    }

    const loadManifestNode = async (hash: string): Promise<Sqlite3mtManifestV2Node | null> => {
        setStage(`build-merge-plan:load-manifest:${hash}:staged`)
        const bytes = (await loadManifestNodeBytesBatch([hash], 'build-merge-plan:load-manifest')).get(hash) ?? null
        if (bytes != null) {
            state.stagedManifestNodes.set(hash, bytes)
        }
        setStage(`build-merge-plan:load-manifest:${hash}:parse`)
        return bytes == null ? null : parseStagedManifestNode(bytes)
    }

    const manifestChildWindow = (children: ReadonlyArray<string | null>, slot: number): string[] => {
        const childIds: string[] = []
        const add = (index: number) => {
            const id = children[index] ?? null
            if (id != null && !state.stagedManifestNodes.has(id) && !childIds.includes(id)) {
                childIds.push(id)
            }
        }
        add(slot)
        for (let distance = 1; distance <= MANIFEST_CHILD_PREFETCH_RADIUS; distance++) {
            add(slot + distance)
            add(slot - distance)
        }
        return childIds
    }

    const resolvePageObjectIdWithManifestReadAhead = async (
        manifestRootId: string | null,
        pageIndex: number,
    ): Promise<string | null> => {
        if (manifestRootId == null || pageIndex < 0) return null

        let currentId: string | null = manifestRootId
        for (let level = 0; level < MANIFEST_V2_DEPTH - 1; level++) {
            if (currentId == null) return null
            const shift = (MANIFEST_V2_DEPTH - 1 - level) * 8
            const slot = (pageIndex >>> shift) & 0xFF
            const node = await loadManifestNode(currentId)
            if (node == null || node.kind !== 'internal') return null

            const childIds = manifestChildWindow(node.children, slot)
            if (childIds.length > 0) {
                await loadManifestNodeBytesBatch(childIds, `build-merge-plan:prefetch-manifest:l${level}:s${slot}`)
            }
            currentId = node.children[slot] ?? null
        }

        if (currentId == null) return null
        const leafSlot = pageIndex & 0xFF
        const leaf = await loadManifestNode(currentId)
        if (leaf == null || leaf.kind !== 'leaf') return null
        return leaf.pages[leafSlot] ?? null
    }

    const getConcreteRootPageHash = async (root: Root, pgno: Pgno): Promise<PageHash | null> => {
        if ((pgno as number) > root.meta.pageCount) {
            return null
        }
        if (root.meta.pageCount === 0 || !root.meta.manifestRootId) return null

        const cacheKey = `${root.rootId}:${pgno as number}`
        if (concreteRootPageHashCache.has(cacheKey)) {
            return concreteRootPageHashCache.get(cacheKey) ?? null
        }

        const pageIndex = (pgno as number) - 1
        const pageObjectId = await resolvePageObjectIdWithManifestReadAhead(root.meta.manifestRootId, pageIndex)
        const hash = pageObjectId as PageHash | null
        concreteRootPageHashCache.set(cacheKey, hash)
        return hash
    }

    return {
        ...manifest,
        setDebugStage(stage: string) {
            setStage(stage)
        },
        async getPageHashFromConcreteRoot(root: Root, pgno: Pgno): Promise<PageHash | null> {
            return getConcreteRootPageHash(root, pgno)
        },
        async diffConcreteRoots(a: Root, b: Root): Promise<Set<Pgno>> {
            if (a.meta.pageCount === 0 && b.meta.pageCount === 0) {
                return new Set<Pgno>()
            }

            const aId = a.meta.pageCount > 0 ? a.meta.manifestRootId : null
            const bId = b.meta.pageCount > 0 ? b.meta.manifestRootId : null

            if (aId === bId) return new Set<Pgno>()

            const maxCount = Math.max(a.meta.pageCount, b.meta.pageCount)
            const minCount = Math.min(a.meta.pageCount, b.meta.pageCount)

            const changed = new Set<Pgno>()
            // Pages beyond the shorter root's pageCount are all changed
            for (let i = minCount; i < maxCount; i++) {
                changed.add((i + 1) as Pgno)
            }

            // Use page-index-aware tree diff — O(changed × depth)
            // instead of the old O(N × depth) per-page resolution pass.
            const diff = await diffManifestTreesWithPageIndices(
                aId || null,
                bId || null,
                loadManifestNode,
            )
            for (const pageIndex of diff.changedPageIndices) {
                if ((pageIndex as number) < maxCount) {
                    changed.add((pageIndex + 1) as Pgno)
                }
            }

            return changed
        },
        async getPageBytesByHash(hash: PageHash): Promise<Uint8Array | null> {
            let bytes = state.stagedCas.get(hash) ?? null
            if (bytes == null) {
                bytes = await durable.getBlob(hash)
            }
            if (bytes == null) {
                bytes = await rpc.rpcGetBlob(hash)
            }
            if (bytes != null) {
                state.stagedCas.set(hash, bytes)
            }
            return bytes
        },
    }
}

function hasInternalReconcileDriverDeps(
    deps: Sqlite3MtLifecycleRuntimeBridgeDeps
): deps is Sqlite3MtLifecycleRuntimeBridgeDeps & {
    manifest: Pick<FullManifestOperations, 'getPageHashFromConcreteRoot'> & PushCapableManifestOps & ReconcileCapableManifestOps
    rpc: Pick<SecureBlobRpcBridge, 'rpcGetBlob' | 'rpcGetHeadVersioned'> & PushCapableRpcBridge
} {
    return hasInternalPushDriverDeps(deps)
        && typeof deps.manifest.diffConcreteRoots === 'function'
        && typeof deps.manifest.getPageBytesByHash === 'function'
}

function pushUploadSetKey(baseRoot: Root, targetRoot: Root): string {
    return `${baseRoot.rootId}->${targetRoot.rootId}`
}

function buildBridgeOwnedPushDriver(
    lifecycle: ReturnType<typeof createControlWorkerLifecycle>,
    deps: Sqlite3MtLifecycleRuntimeBridgeDeps & {
        manifest: Pick<FullManifestOperations, 'getPageHashFromConcreteRoot'> & PushCapableManifestOps
        rpc: Pick<SecureBlobRpcBridge, 'rpcGetBlob' | 'rpcGetHeadVersioned'> & PushCapableRpcBridge
    }
): (reason: SyncEngineKickReason) => void {
    let queued = false
    let scheduled = false
    let running = false
    const uploadSets = new Map<string, { pageBlobHashes: Set<PageHash>; manifestNodeHashes: Set<string> }>()
    const logSyncDriver = (event: string, extra?: Record<string, unknown>): void => {
        const walWriteHolders = Atomics.load(
            sab4ScalarView(lifecycle.sabs.sab4),
            Sab4Scalar.WAL_WRITE_HOLDERS,
        )
        sdkConsole.trace('[sqlite3mt:engine] sync-driver', {
            workspaceRootId: lifecycle.state.workspaceBaseHead.root.rootId || '<empty>',
            runtimeEpoch: lifecycle.runtime.runtimeEpoch.toString(),
            event,
            pushBusy: lifecycle.state.pushBusy,
            mergeBusy: lifecycle.state.mergeBusy,
            externalClientRequestCount: lifecycle.state.externalClientRequestCount,
            walWriteHolders,
            overlaySize: lifecycle.state.workspaceOverlay.size,
            remoteVersion: lifecycle.state.remoteHead.version.toString(),
            baseVersion: lifecycle.state.workspaceBaseHead.version.toString(),
            ...extra,
        })
    }

    const queueSyncPass = (reason: string): void => {
        ;(lifecycle.state as ControlWorkerRuntimeState & {
            __debugSyncDriverLastQueue?: Record<string, unknown>
        }).__debugSyncDriverLastQueue = {
            reason,
        }
        queued = true
        logSyncDriver('queue', { reason })
        if (scheduled || running) {
            return
        }

        scheduled = true
        void Promise.resolve().then(async () => {
            scheduled = false
            if (running) {
                return
            }

            running = true
            try {
                while (queued) {
                    queued = false

                    if (lifecycle.state.syncFatalError != null) {
                        continue
                    }

                    ;(lifecycle.state as ControlWorkerRuntimeState & {
                        __debugTransportStageGetter?: (() => string | null) | undefined
                    }).__debugTransportStageGetter = (deps.rpc as RpcDebugStageCapable).getDebugStage
                    ;(lifecycle.state as ControlWorkerRuntimeState & {
                        __debugTransportDetailsGetter?: (() => unknown) | undefined
                    }).__debugTransportDetailsGetter = (deps.rpc as RpcDebugStageCapable).getDebugDetails

                    const walWriteHolders = Atomics.load(
                        sab4ScalarView(lifecycle.sabs.sab4),
                        Sab4Scalar.WAL_WRITE_HOLDERS,
                    )
                    if (walWriteHolders > 0) {
                        logSyncDriver('decision', { decision: 'IDLE_WAL_WRITE', walWriteHolders })
                        continue
                    }

                    const decision = maybeStartPushOrImport(lifecycle.state)
                    logSyncDriver('decision', { decision: decision.kind })
                    if (decision.kind === 'START_RECONCILE') {
                        if (reconcileHooks != null) {
                            await runRemoteReconcile(lifecycle.state, reconcileHooks)
                        }
                        continue
                    }
                    if (decision.kind !== 'START_PUSH') {
                        if (lifecycle.state.checkpointWanted) {
                            await lifecycle.maybeRequestCheckpoint('sync-driver-idle')
                        }
                        continue
                    }

                    await runPush(lifecycle.state, pushHooks)
                }
            } catch (error) {
                lifecycle.enterFatalRuntime(error, SQLITE3MT_RUNTIME_DISPATCH_FAILED)
            } finally {
                running = false
                if (queued && !scheduled) {
                    queueSyncPass('post-busy-epilog')
                }
            }
        })
    }

    const pushHooks: RunPushHooks = {
        normalizeSparseOverlay(baseRoot, overlay, pageCount) {
            return deps.manifest.normalizeSparseOverlay(baseRoot, overlay, pageCount)
        },

        async materializeRoot(baseRoot, overlay, pageCount, parentPublicRoot) {
            const overlayBytes = new Map<Pgno, Uint8Array>()
            const overlayHashes = new Set<PageHash>()

            for (const [pgno, hash] of overlay) {
                overlayHashes.add(hash)
                const bytes = await resolveSqlite3MtPushPageBlob(hash, {
                    stagedCas: lifecycle.state.stagedCas,
                    getBlob: (h) => deps.durable.getBlob(h),
                    rpcGetBlob: (h) => deps.rpc.rpcGetBlob(h),
                })
                if (bytes == null) {
                    // Local CAS / durable may have lost a page blob that the
                    // overlay still references (worker crash, partial IDB GC,
                    // staged eviction). resolveSqlite3MtPushPageBlob already
                    // tried remote; reaching here means genuine data loss.
                    throw new Error(`${SQLITE3MT_PUSH_BLOB_MISSING}: ${hash}`)
                }
                overlayBytes.set(pgno, bytes)
            }

            const result = await deps.manifest.materializeRoot(
                baseRoot,
                overlayBytes,
                pageCount,
                {
                    root: parentPublicRoot,
                    version: lifecycle.state.workspaceBaseHead.version,
                }
            )

            uploadSets.set(pushUploadSetKey(baseRoot, result.root), {
                pageBlobHashes: new Set(
                    Array.from(result.uploadSet.pageBlobHashes, hash => hash as PageHash)
                ),
                manifestNodeHashes: new Set(result.uploadSet.manifestNodeHashes),
            })

            const manifestNodeBlobsByHash = new Map<string, Uint8Array>()
            for (const { hash, bytes } of result.objects) {
                if (!overlayHashes.has(hash as PageHash)) {
                    manifestNodeBlobsByHash.set(hash, bytes)
                }
            }

            return {
                root: result.root,
                manifestNodeBlobsByHash,
            }
        },

        collectUploadSet(baseRoot, targetRoot) {
            return uploadSets.get(pushUploadSetKey(baseRoot, targetRoot)) ?? {
                pageBlobHashes: new Set<PageHash>(),
                manifestNodeHashes: new Set<string>(),
            }
        },

        async rpcPublishIfCurrent(expectedRoot, expectedVersion, nextRoot, publishPrefetchHints) {
            const result = await deps.rpc.rpcPublishIfCurrent(
                expectedRoot,
                expectedVersion,
                nextRoot,
                publishPrefetchHints
            )

            if (result.ok) {
                return {
                    ok: true,
                    currentRoot: nextRoot,
                    currentVersion: expectedVersion + 1n,
                }
            }

            return {
                ok: false,
                currentRoot: result.currentRoot,
                currentVersion: result.currentVersion,
            }
        },

        async rpcPutMissingBlobs(hashes, loader) {
            await deps.rpc.rpcPutMissingBlobs(hashes, async hash => {
                const bytes = await loader(hash as PageHash)
                if (bytes == null) {
                    throw new Error(`${SQLITE3MT_PUSH_BLOB_MISSING}: ${hash}`)
                }
                return bytes
            })
        },

        async rpcPutMissingManifestNodes(hashes, loader) {
            await deps.rpc.rpcPutMissingManifestNodes(hashes, async hash => {
                const bytes = await loader(hash)
                if (bytes == null) {
                    throw new Error(`SQLITE3MT_PUSH_MANIFEST_MISSING: ${hash}`)
                }
                return bytes
            })
        },

        putManifestNode(hash, bytes) {
            return deps.durable.putManifestNode(hash, bytes)
        },

        beginHeadAdvancePreparationAndDrain() {
            return beginHeadAdvancePreparationAndDrain(lifecycle.state, lifecycle.state)
        },

        endHeadAdvancePreparation() {
            endHeadAdvancePreparation(lifecycle.state)
        },

        captureDurableSnapshot() {
            return captureDurableSnapshot(lifecycle.state, {
                requiredBlobHashes: lifecycle.state.stagedCas.keys(),
                requiredManifestNodeHashes: lifecycle.state.stagedManifestNodes.keys(),
            })
        },

        persistSnapshotSerial(snapshot, truncateJournal) {
            return persistSnapshotSerial({
                state: lifecycle.state,
                runtime: lifecycle.state,
                durable: deps.durable,
                snapshot: snapshot as DurableLocalSnapshot,
                truncateJournal,
            })
        },

        requestPersistLocalSnapshot() {
            void lifecycle.requestPersistLocalSnapshot?.()
        },

        applyRemoteHeadIfNewer(root, version, reqSeq) {
            return applyRemoteHeadIfNewer(
                lifecycle.state,
                root,
                version,
                reqSeq,
                {
                    maybeStartPushOrImport: () => queueSyncPass('remote-head-applied'),
                    enterFatalRuntime: lifecycle.enterFatalRuntime,
                }
            )
        },

        gcStagedIfSafe() {
            pruneStagedSqlite3MtCasToSnapshotRequirements(
                lifecycle.state.stagedCas,
                lifecycle.state.stagedManifestNodes,
                captureDurableSnapshot(lifecycle.state),
            )
        },

        getStagedBlob(hash) {
            return lifecycle.state.stagedCas.get(hash)
        },

        getStagedManifestNode(hash) {
            return lifecycle.state.stagedManifestNodes.get(hash)
        },

        getDurableBlob(hash) {
            return deps.durable.getBlob(hash)
        },

        getDurableManifestNode(hash) {
            return deps.durable.getManifestNode(hash)
        },

        abortIfFatal() {
            abortIfFatal(lifecycle.state)
        },

        enterFatalRuntime(error, reason) {
            lifecycle.enterFatalRuntime(error, reason)
        },

        isTransientSyncError,

        scheduleSyncRetry() {
            const retryJitterMs = Number(lifecycle.runtime.runtimeEpoch % 11n) * 17
            scheduleSyncRetry(
                lifecycle.state,
                lifecycle.state.syncRetryDelayMs + retryJitterMs,
                () => {
                    void lifecycle.runtime.handleMessage({
                        type: 'sync-retry-fired',
                        runtimeEpoch: lifecycle.runtime.runtimeEpoch,
                    }).catch(error => {
                        lifecycle.enterFatalRuntime(error, SQLITE3MT_RUNTIME_DISPATCH_FAILED)
                    })
                },
                deps.setTimeoutFn
            )
        },

        maybeStartPushOrImport() {
            queueSyncPass('hook')
        },

        maybeRequestCheckpoint(reason) {
            return lifecycle.maybeRequestCheckpoint(reason)
        },
    }

    // ── §15.9 Reconcile hooks (conditional on reconcile-capable deps) ──
    const reconcileHooks: RunReconcileHooks | null =
        typeof deps.manifest.diffConcreteRoots === 'function'
        && typeof deps.manifest.getPageBytesByHash === 'function'
        ? buildReconcileHooks()
        : null

    function buildReconcileHooks(): RunReconcileHooks {
        const state = lifecycle.state
        const sabs = lifecycle.sabs
        const reconcileManifest = createReconcileManifestView(
            state,
            deps.manifest as unknown as ManifestOperations,
            deps.durable,
            deps.rpc,
        )
        let reconcileIntentCounter = BigInt(Date.now()) * 1_000_000n

        return {
            // ── Shared with pushHooks ────────────────────────────────
            normalizeSparseOverlay: pushHooks.normalizeSparseOverlay,
            beginHeadAdvancePreparationAndDrain: pushHooks.beginHeadAdvancePreparationAndDrain,
            endHeadAdvancePreparation: pushHooks.endHeadAdvancePreparation,
            captureDurableSnapshot: pushHooks.captureDurableSnapshot,
            persistSnapshotSerial: pushHooks.persistSnapshotSerial,
            requestPersistLocalSnapshot: pushHooks.requestPersistLocalSnapshot,
            gcStagedIfSafe: pushHooks.gcStagedIfSafe,
            getStagedBlob: pushHooks.getStagedBlob,
            getDurableBlob: pushHooks.getDurableBlob,
            abortIfFatal: pushHooks.abortIfFatal,
            enterFatalRuntime: pushHooks.enterFatalRuntime,
            isTransientSyncError: pushHooks.isTransientSyncError,
            scheduleSyncRetry: pushHooks.scheduleSyncRetry,
            maybeStartPushOrImport: pushHooks.maybeStartPushOrImport,

            // ── §15.3 Three-way merge ───────────────────────────────
            async buildMergePlan(args) {
                return buildMergePlan(reconcileManifest, args)
            },

            buildResidualOverlayAgainstRemote(plan) {
                return buildResidualOverlayAgainstRemote(plan)
            },

            async ensureMetadataCarrierFrameIfNeeded(plan) {
                await ensureMetadataCarrierFrameIfNeeded(
                    plan,
                    {
                        stagedCas: state.stagedCas,
                        getBlob(hash: PageHash) { return deps.durable.getBlob(hash) },
                        rpcGetBlob(hash: PageHash) { return deps.rpc.rpcGetBlob(hash) },
                    },
                    reconcileManifest
                )
            },

            // ── §15.9 WAL / advanceDbHead ────────────────────────────
            advanceDbHead() {
                return lifecycle.advanceDbHead()
            },

            // ── §15.7 Reconcile import under WAL_WRITE ──────────────
            async tryImportPreparedIntentAsSingleWalCommit({ intent, plan }) {
                const lockView = sab3LockView(sabs.sab3)

                return tryImportPreparedIntentAsSingleWalCommit({
                    intent,
                    plan,
                    state,
                    runtime: state,
                    durable: deps.durable,
                    hooks: {
                        tryAcquireWalWriteLock() {
                            return Atomics.compareExchange(lockView, 0, 0, -1) === 0
                        },
                        releaseWalWriteLock() {
                            Atomics.store(lockView, 0, 0)
                        },
                        readWalSnapshotStable() {
                            return readWalSnapshotStable(sabs.sab3)
                        },
                        preflightAppendWalFramesSingleTransaction(importBlobs) {
                            const views = createWalWriterViews(sabs)
                            const capacity = ensureWalFrameAppendCapacity(views, importBlobs.size)
                            return capacity.canAppend
                                ? null
                                : { kind: 'WAL_CAPACITY_BUSY', ...capacity }
                        },
                        async appendWalFramesSingleTransaction(
                            importBlobs: ReadonlyMap<Pgno, Uint8Array>,
                            targetPostPageCount: number
                        ): Promise<AppendWalFramesMeta> {
                            const views = createWalWriterViews(sabs)
                            const currentPage1 = importBlobs.get(1 as Pgno) ?? null
                            const appendResult = appendWalFrames(
                                views,
                                new Map(importBlobs),
                                targetPostPageCount,
                                currentPage1
                            )
                            const orderedPgnos = Array.from(importBlobs.keys()).sort((a, b) => a - b)
                            const newFrames = orderedPgnos.map((pgno, index) => ({
                                pgno,
                                frameIdx: appendResult.newMxFrame - orderedPgnos.length + index + 1,
                            }))
                            updateWalIndex(views, newFrames)
                            const walHeader = parseWalHeader(views.walDataView.subarray(0, WAL_HDR_SIZE))
                            if (walHeader != null) {
                                updateWalIndexHdr(
                                    views,
                                    appendResult.newMxFrame,
                                    targetPostPageCount,
                                    appendResult.finalCksum,
                                    walHeader
                                )
                            }
                            return {
                                commitFrameMax: appendResult.newMxFrame,
                                postWalSalt0: walHeader?.salt1 ?? 0,
                                postWalSalt1: walHeader?.salt2 ?? 0,
                            }
                        },
                        enterFatalRuntime: pushHooks.enterFatalRuntime,
                    },
                    sab1: sabs.sab1,
                    sab4: sabs.sab4,
                    pageSize: sabs.pageSize,
                })
            },

            // ── §15.8 Catch-up when WAL grows during reconcile ───────
            async catchUpMergePlanToFrame(
                plan: MergePlan,
                targetFrameMax: number
            ): Promise<void> {
                let caughtUp = false
                for (const [frameMax, delta] of state.pendingDbDeltas) {
                    if (frameMax <= plan.caughtUpFrameMax) continue
                    if (frameMax > targetFrameMax as unknown as FrameMax) break

                    for (const [pgno, hash] of delta.pageHashes) {
                        if (hash != null) {
                            plan.liveLocalNorm.set(pgno, hash)
                        } else {
                            plan.liveLocalNorm.delete(pgno)
                        }
                    }
                    if (delta.postPageCount != null) {
                        plan.liveLocalPageCount = delta.postPageCount
                    }
                    caughtUp = true
                }
                plan.caughtUpFrameMax = targetFrameMax
                if (!caughtUp) return

                const rebuilt = await buildMergePlan(reconcileManifest, {
                    baseRoot: plan.baseRoot,
                    baseVersion: plan.baseVersion,
                    remoteRoot: plan.remoteRoot,
                    remoteVersion: plan.remoteVersion,
                    wal: {
                        salt0: plan.walSalt0,
                        salt1: plan.walSalt1,
                        iChange: 0,
                        mxFrame: targetFrameMax,
                        nBackfill: 0,
                    },
                    localNorm: plan.liveLocalNorm,
                    localPageCount: plan.liveLocalPageCount,
                    pendingDbDeltasIntegratedFrameMax: targetFrameMax,
                })

                plan.liveLocalNorm = rebuilt.liveLocalNorm
                plan.liveLocalPageCount = rebuilt.liveLocalPageCount
                plan.remoteChanged = rebuilt.remoteChanged
                plan.remoteHashes = rebuilt.remoteHashes
                plan.baseHashes = rebuilt.baseHashes
                plan.resultHashByPgno = rebuilt.resultHashByPgno
                plan.resultPageCount = rebuilt.resultPageCount
                plan.importBlobsByPgno = rebuilt.importBlobsByPgno
                plan.importBlobsByHash = rebuilt.importBlobsByHash
                plan.basePagesCache = rebuilt.basePagesCache
                plan.remotePagesCache = rebuilt.remotePagesCache
                plan.caughtUpFrameMax = targetFrameMax
            },

            async ensureReconcileIntentBlobsDurable(plan: MergePlan) {
                for (const [hash, bytes] of plan.importBlobsByHash) {
                    const existing = await deps.durable.getBlob(hash)
                    if (existing == null) {
                        await deps.durable.putBlob(hash, bytes)
                    }
                }
            },

            // ── §15.8 Reconcile tail / orphan detection ─────────────
            reconcileTailCanStillIntegrateSoon() {
                return reconcileTailCanStillIntegrateSoon(sabs.sab4, state.inflightCommitRegistrations)
            },

            hasOutstandingCommitPosts() {
                return hasAnyOutstandingCommitPosts(sabs.sab4)
            },

            async awaitOutstandingCommitPostsToChangeOrEnterFatal(reason: string) {
                await awaitOutstandingCommitPostsToChangeOrEnterFatal({
                    state,
                    runtime: state,
                    sab4: sabs.sab4,
                    hooks: {
                        yieldOneTaskTurnForPendingCommitMessages:
                            deps.yieldOneTaskTurnForPendingCommitMessages ?? yieldOneTaskTurn,
                        enterFatalRuntime: pushHooks.enterFatalRuntime,
                    },
                    fatalReason: reason,
                })
            },

            yieldOneTaskTurnForPendingCommitMessages() {
                return (deps.yieldOneTaskTurnForPendingCommitMessages ?? yieldOneTaskTurn)()
            },

            reconcileOrphanProbeState(live) {
                return reconcileOrphanProbeState(live, state.integratedFrameMax)
            },

            sameReconcileOrphanProbeState(a, b) {
                return sameReconcileOrphanProbeState(a, b)
            },

            sameWalSalt(a0, a1, b0, b1) {
                return sameWalSalt(a0, a1, b0, b1)
            },

            // ── §6.4 Durable intent management ──────────────────────
            nextIntentId() {
                reconcileIntentCounter += 1n
                return reconcileIntentCounter
            },

            async writeReconcileIntent(intent) {
                await deps.durable.writeReconcileIntent(intent)
            },

            async abandonPreparedReconcileIntent(intent) {
                await deps.durable.clearReconcileIntent(intent.intentId)
                state.reconcileIntent = null
            },

            // ── §15.9 Reconcile base advance ────────────────────────
            startPendingReconcileBaseAdvance(intent, residual, appendMeta) {
                if (state.pendingBaseAdvance != null) {
                    throw new Error(INTERNAL_PENDING_BASE_ADVANCE_REENTRY)
                }
                state.pendingBaseAdvance = {
                    kind: 'RECONCILE',
                    intentId: intent.intentId,
                    promise: (async () => {
                        state.workspaceBaseHead = {
                            root: intent.remoteRoot,
                            version: intent.remoteVersion,
                        }
                        state.workspaceOverlay = new Map(residual.pages)
                        state.workspacePageCount = residual.pageCount
                        if (appendMeta != null) {
                            await deps.durable.markReconcileIntentCommitted(
                                intent.intentId,
                                {
                                    commitFrameMax: appendMeta.commitFrameMax,
                                    commitPostWalSalt0: appendMeta.postWalSalt0,
                                    commitPostWalSalt1: appendMeta.postWalSalt1,
                                }
                            )
                        }
                        state.reconcileIntent = null
                        await pushHooks.persistSnapshotSerial(
                            pushHooks.captureDurableSnapshot(),
                            true
                        )
                    })(),
                }
            },

            async flushPendingBaseAdvanceIfAny() {
                await flushPendingBaseAdvanceIfAny(state, pushHooks.enterFatalRuntime)
            },

            // ── Fatal / cleanup ──────────────────────────────────────
            enterUncertainAppendingFatal(enterFatal) {
                enterUncertainAppendingFatal(enterFatal)
            },

            maybeRequestCheckpoint(reason) {
                void lifecycle.maybeRequestCheckpoint(reason)
            },

            getSab1() { return sabs.sab1 },
            getSab4() { return sabs.sab4 },
        }
    }

    return (reason) => {
        queueSyncPass(reason)
    }
}

function countActiveSlots(sab1: SharedArrayBuffer, pageSize: number): number {
    let used = 0
    for (let slot = 0; slot < NUM_SLOTS; slot += 1) {
        if (Atomics.load(sab1SlotMetaView(sab1, slot, pageSize), SlotMetaI32.STATE) !== SlotState.EMPTY) {
            used += 1
        }
    }
    return used
}

function createMemoryManagerCompat(context: RuntimeContext): SqliteMemoryManagerCompat {
    return {
        sab1: context.sabs.sab1,
        sab2: context.sabs.sab2,
        sab3: context.sabs.sab3,
        isSabMode: true,
        autoCheckpointThreshold: 0,
        get pageSize() {
            return context.sabs.pageSize
        },
        get dbPageCount() {
            return Atomics.load(sab1CtrlView(context.sabs.sab1), SAB1_DB_PAGE_COUNT_I32)
        },
        get walDataSize() {
            return Atomics.load(sab2SizeView(context.sabs.sab2), 0)
        },
        get pageWindowSlots() {
            return NUM_SLOTS
        },
        get pageWindowUsed() {
            return countActiveSlots(context.sabs.sab1, context.sabs.pageSize)
        },
        get dirtyPageCount() {
            return context.runtimeOwnerHooks?.getDirtyPages().length ?? 0
        },
        lockView: sab3LockView(context.sabs.sab3),
        shmRegion(iRegion: number, regionSize: number): Uint8Array | null {
            if (!Number.isInteger(iRegion) || iRegion < 0 || !Number.isInteger(regionSize) || regionSize <= 0) {
                return null
            }

            const shm = sab3ShmView(context.sabs.sab3)
            const start = iRegion * regionSize
            const end = start + regionSize
            if (end > shm.byteLength) {
                return null
            }

            return shm.subarray(start, end)
        },
        isMode0Active() {
            return false
        },
        isDrainPending() {
            return false
        },
        isImportPending() {
            return false
        },
    }
}

function resolveCheckpointModeConstant(
    sqlite3: Sqlite3WasmRuntimeApi,
    mode: CheckpointMode
): number {
    const capi = sqlite3.capi as Sqlite3CheckpointCapableCapi

    switch (mode) {
        case 'PASSIVE':
            return capi.SQLITE_CHECKPOINT_PASSIVE ?? 0
        case 'RESTART':
            return capi.SQLITE_CHECKPOINT_RESTART ?? 2
        case 'TRUNCATE':
            return capi.SQLITE_CHECKPOINT_TRUNCATE ?? 3
    }
}

function allocCString(
    sqlite3: Sqlite3WasmRuntimeApi,
    bytes: Uint8Array
): number {
    const ptr = sqlite3.wasm.alloc(bytes.byteLength)
    sqlite3.wasm.heap8u().set(bytes, ptr)
    return ptr
}

function toCheckpointCount(value: unknown, fallback: number): number {
    return typeof value === 'number' && Number.isFinite(value)
        ? value
        : fallback
}

function runWalCheckpointViaPragma(
    handle: SqliteEngineHandle,
    mode: CheckpointMode
): CheckpointWorkerResult {
    const row = handle.query<Record<string, unknown>>(`PRAGMA wal_checkpoint(${mode})`)[0] ?? {}
    return {
        rc: toCheckpointCount(row.busy, 0),
        logFrames: toCheckpointCount(row.log ?? row.logFrames, -1),
        ckptFrames: toCheckpointCount(row.checkpointed ?? row.ckptFrames, -1),
    }
}

function runWalCheckpointOnHandle(
    sqlite3: Sqlite3WasmRuntimeApi,
    handle: SqliteEngineHandle,
    mode: CheckpointMode
): CheckpointWorkerResult {
    const capi = sqlite3.capi as Sqlite3CheckpointCapableCapi

    if (typeof capi.sqlite3_wal_checkpoint_v2 === 'function') {
        const dbNamePtr = allocCString(sqlite3, SQLITE_MAIN_DB_NAME)
        const logFramesPtr = sqlite3.wasm.alloc(4)
        const ckptFramesPtr = sqlite3.wasm.alloc(4)

        try {
            sqlite3.wasm.poke32(logFramesPtr, -1)
            sqlite3.wasm.poke32(ckptFramesPtr, -1)

            const rc = capi.sqlite3_wal_checkpoint_v2(
                handle.dbPtr,
                dbNamePtr,
                resolveCheckpointModeConstant(sqlite3, mode),
                logFramesPtr,
                ckptFramesPtr,
            )

            return {
                rc,
                logFrames: sqlite3.wasm.peek32(logFramesPtr),
                ckptFrames: sqlite3.wasm.peek32(ckptFramesPtr),
            }
        } finally {
            sqlite3.wasm.dealloc(ckptFramesPtr)
            sqlite3.wasm.dealloc(logFramesPtr)
            sqlite3.wasm.dealloc(dbNamePtr)
        }
    }

    sdkConsole.trace('[sqlite3mt:engine] checkpoint:capi-unavailable-falling-back-to-pragma', {
        mode,
        dbPtr: handle.dbPtr,
    })
    return runWalCheckpointViaPragma(handle, mode)
}

function allocateConnId(activeConnIds: ReadonlySet<number>): number {
    for (let connId = 0; connId < MAX_CONN; connId += 1) {
        if (!activeConnIds.has(connId)) {
            return connId
        }
    }

    throw new RangeError(`sqlite3mt runtime exhausted all ${MAX_CONN} connection slots`)
}

function queryRows<T>(db: Sqlite3Oo1Database, sql: string, params?: unknown[]): T[] {
    const rows: T[] = []
    const options: {
        sql: string
        bind?: unknown[]
        rowMode: string
        callback: (row: unknown) => void
    } = {
        sql,
        rowMode: 'object',
        callback: row => {
            rows.push(row as T)
        },
    }
    if (params != null && params.length > 0) {
        options.bind = params
    }
    db.exec(options)
    return rows
}

function normalizeBlobBytes(value: unknown): Uint8Array {
    if (value instanceof Uint8Array) {
        return new Uint8Array(value)
    }
    if (ArrayBuffer.isView(value)) {
        return new Uint8Array(value.buffer, value.byteOffset, value.byteLength)
    }
    if (value instanceof ArrayBuffer) {
        return new Uint8Array(value)
    }
    if (value != null && typeof value === 'object' && 'length' in value) {
        return Uint8Array.from(value as ArrayLike<number>)
    }

    throw new TypeError('Expected sqlite_dbpage BLOB bytes')
}

function queryPragmaNumber(
    db: Sqlite3Oo1Database,
    pragmaName: 'page_count' | 'page_size',
    fallback = 0
): number {
    const row = queryRows<Record<string, number>>(db, `PRAGMA ${pragmaName}`)[0]
    const value = row?.[pragmaName]
    return typeof value === 'number' && Number.isFinite(value) ? value : fallback
}

function queryPragmaText(
    db: Sqlite3Oo1Database,
    pragmaName: 'journal_mode',
): string | null {
    const row = queryRows<Record<string, unknown>>(db, `PRAGMA ${pragmaName}`)[0]
    const value = row?.[pragmaName]
    return typeof value === 'string' ? value : null
}

function readSqliteDbPages(
    db: Sqlite3Oo1Database,
    requestedPgnos: readonly number[]
): Map<number, Uint8Array> {
    const normalizedPgnos = Array.from(new Set(
        requestedPgnos.filter(pgno => Number.isInteger(pgno) && pgno > 0)
    )).sort((left, right) => left - right)

    if (normalizedPgnos.length === 0) {
        return new Map()
    }

    const placeholders = normalizedPgnos.map(() => '?').join(', ')
    const rows = queryRows<{ pgno: number, data: unknown }>(
        db,
        `SELECT pgno, data FROM sqlite_dbpage WHERE pgno IN (${placeholders}) ORDER BY pgno`,
        normalizedPgnos
    )

    const byPgno = new Map<number, Uint8Array>()
    for (const row of rows) {
        byPgno.set(row.pgno, normalizeBlobBytes(row.data))
    }

    const result = new Map<number, Uint8Array>()
    for (const pgno of normalizedPgnos) {
        const bytes = byPgno.get(pgno)
        if (bytes != null) {
            result.set(pgno, bytes)
        }
    }

    return result
}

function exportSqliteDbBytes(
    db: Sqlite3Oo1Database,
    fallbackPageSize: number
): Uint8Array {
    const pageCount = queryPragmaNumber(db, 'page_count')
    if (pageCount <= 0) {
        return new Uint8Array(0)
    }

    const pageSize = queryPragmaNumber(db, 'page_size', fallbackPageSize) || fallbackPageSize
    const pages = readSqliteDbPages(
        db,
        Array.from({ length: pageCount }, (_, index) => index + 1)
    )
    const bytes = new Uint8Array(pageCount * pageSize)

    for (const [pgno, pageBytes] of pages) {
        bytes.set(
            pageBytes.subarray(0, Math.min(pageBytes.length, pageSize)),
            (pgno - 1) * pageSize
        )
    }

    return bytes
}

function createWalWriterViews(sabs: SabSet): WalFrameSabViews {
    return createWalFrameSabViews(sabs.sab2, sabs.sab3, sabs.pageSize, sabs.sab4)
}

function createBuiltinHandleHooks(
    runtime: RuntimeContext,
    connState: ClientWorkerConnState,
    db: Sqlite3Oo1Database
): Sqlite3MtHandleHooks {
    return {
        getDirtyPages() {
            return []
        },

        resetDirtyTracking() {},

        exportFullDb() {
            return exportSqliteDbBytes(db, runtime.sabs.pageSize)
        },

        importFullDb(bytes: Uint8Array) {
            const ctrl = sab1CtrlView(runtime.sabs.sab1)
            const pageSize = runtime.sabs.pageSize
            const nPages = Math.ceil(bytes.length / pageSize)

            // Update the DB page count so slot pinning sees the right EOF.
            Atomics.store(ctrl, SAB1_DB_PAGE_COUNT_I32, nPages)

            for (let i = 0; i < nPages; i++) {
                const pgno = i + 1
                const offset = i * pageSize
                const pageBytes = bytes.slice(offset, offset + pageSize)
                writeSlotPage(pgno, pageBytes, runtime.sabs.sab1, pageSize)
            }
        },

        exportDirtyPages() {
            return new Map()
        },

        importMergedPages(pages: Map<number, Uint8Array>) {
            if (pages.size === 0) {
                return
            }

            const ctrl = sab1CtrlView(runtime.sabs.sab1)
            let targetPageCount = Atomics.load(ctrl, SAB1_DB_PAGE_COUNT_I32)

            for (const [pgno, pageBytes] of pages) {
                writeSlotPage(pgno, pageBytes, runtime.sabs.sab1, runtime.sabs.pageSize)
                if (pgno > targetPageCount) {
                    targetPageCount = pgno
                }
            }

            Atomics.store(ctrl, SAB1_DB_PAGE_COUNT_I32, targetPageCount)
        },

        appendWalFramesForSync(pages: Map<number, Uint8Array>) {
            if (pages.size === 0) {
                return
            }

            runtime.registrations.get(connState.connId)?.pushMappedShmRegionsToShared()

            const ctrl = sab1CtrlView(runtime.sabs.sab1)
            let targetPageCount = Atomics.load(ctrl, SAB1_DB_PAGE_COUNT_I32)
            for (const pgno of pages.keys()) {
                if (pgno > targetPageCount) {
                    targetPageCount = pgno
                }
            }

            const views = createWalWriterViews(runtime.sabs)
            const currentPage1 = readSqliteDbPages(db, [1]).get(1) ?? null
            const appendResult = appendWalFrames(
                views,
                pages,
                targetPageCount,
                currentPage1
            )

            const orderedPgnos = Array.from(pages.keys()).sort((left, right) => left - right)
            const newFrames = orderedPgnos.map((pgno, index) => ({
                pgno,
                frameIdx: appendResult.newMxFrame - orderedPgnos.length + index + 1,
            }))
            updateWalIndex(views, newFrames)

            const walHeader = parseWalHeader(views.walDataView.subarray(0, WAL_HDR_SIZE))
            if (walHeader != null) {
                updateWalIndexHdr(
                    views,
                    appendResult.newMxFrame,
                    targetPageCount,
                    appendResult.finalCksum,
                    walHeader
                )
            }

            for (const registration of runtime.registrations.values()) {
                registration.pullMappedShmRegionsFromShared()
            }
        },

        async readPages(pgnos: number[]) {
            return readSqliteDbPages(db, pgnos)
        },
    }
}

export async function createSqlite3MtLifecycleRuntimeBridge(
    args: CreateSqlite3MtRuntimeArgs,
    deps: Sqlite3MtLifecycleRuntimeBridgeDeps
): Promise<Sqlite3MtRuntimeBridge> {
    let lifecycle!: ReturnType<typeof createControlWorkerLifecycle>
    let runtimeBridge: Sqlite3MtRuntimeBridge | null = null
    let syntheticWalSnapshot: WalSnapshot | null = null
    let requestSyncEngine: (reason: SyncEngineKickReason) => void =
        deps.kickSyncEngine ?? noopKickSyncEngine
    let localBackgroundSyncTimer: unknown | null = null
    let localBackgroundSyncGeneration = 0
    const localBackgroundSyncEnabled = args.config.backgroundSync !== 'manual'
    const setTimeoutFn = deps.setTimeoutFn
        ?? ((callback: () => void, delayMs: number) => globalThis.setTimeout(callback, delayMs))

    const clearLocalBackgroundSyncTimer = (): void => {
        localBackgroundSyncGeneration += 1
        if (localBackgroundSyncTimer != null) {
            globalThis.clearTimeout(localBackgroundSyncTimer as ReturnType<typeof globalThis.setTimeout>)
            localBackgroundSyncTimer = null
        }
    }

    const requestSyncEngineWithPolicy = (reason: SyncEngineKickReason): void => {
        ;(lifecycle.state as ControlWorkerRuntimeState & {
            __debugSyncPolicyLastRequest?: Record<string, unknown>
        }).__debugSyncPolicyLastRequest = {
            reason,
            backgroundSync: args.config.backgroundSync ?? 'workspace-policy',
            localBackgroundSyncEnabled,
            localMaintenance: LOCAL_MAINTENANCE_REASONS.has(reason),
            localBackground: LOCAL_BACKGROUND_SYNC_REASONS.has(reason),
        }
        if (LOCAL_MAINTENANCE_REASONS.has(reason)) {
            void requestLocalMaintenance(reason)
            return
        }

        if (LOCAL_BACKGROUND_SYNC_REASONS.has(reason)) {
            if (!localBackgroundSyncEnabled) {
                clearLocalBackgroundSyncTimer()
                return
            }
            if (localBackgroundSyncTimer != null) {
                return
            }

            const generation = localBackgroundSyncGeneration
            localBackgroundSyncTimer = setTimeoutFn(() => {
                if (generation !== localBackgroundSyncGeneration) {
                    return
                }

                localBackgroundSyncTimer = null
                requestSyncEngine(reason)
            }, LOCAL_BACKGROUND_SYNC_DEBOUNCE_MS)
            return
        }

        clearLocalBackgroundSyncTimer()
        requestSyncEngine(reason)
    }

    const requestLocalMaintenance = async (reason: SyncEngineKickReason): Promise<void> => {
        if (lifecycle.state.syncFatalError != null) {
            return
        }
        if (lifecycle.state.checkpointWanted || lifecycle.state.pendingDbDeltas.size > 0) {
            await lifecycle.maybeRequestCheckpoint(reason)
        }
        await lifecycle.requestPersistLocalSnapshot?.()
    }

    const requestSyncEngineAfterCheckpointOpportunity = (reason: SyncEngineKickReason): void => {
        void (async () => {
            if (lifecycle.state.checkpointWanted) {
                await lifecycle.maybeRequestCheckpoint(reason)
            }
            requestSyncEngineWithPolicy(reason)
        })().catch(error => {
            lifecycle.enterFatalRuntime(error, SQLITE3MT_RUNTIME_DISPATCH_FAILED)
        })
    }

    const hasExternalWalSnapshotReader =
        deps.readLiveWalSnapshot != null || deps.tryReadWalSnapshotStableOrNull != null

    const updateSyntheticWalDebug = (
        stage: string,
        details?: Record<string, unknown>,
    ): void => {
        if (lifecycle == null) {
            return
        }
        ;(lifecycle.state as ControlWorkerRuntimeState & {
            __debugSyntheticWalSnapshot?: Record<string, unknown>
        }).__debugSyntheticWalSnapshot = {
            stage,
            hasExternalWalSnapshotReader,
            syntheticWalSnapshot,
            lastWalSnapshot: lifecycle.state.lastWalSnapshot,
            ...details,
        }
    }

    const readInternalSharedWalSnapshot = (): WalSnapshot | null =>
        tryReadInitializedSharedWalSnapshot(args.sabs)

    const readObservedBootstrapWalSnapshot = (): WalSnapshot | null => cloneWalSnapshot(
        deps.tryReadWalSnapshotStableOrNull?.()
        ?? syntheticWalSnapshot
    )

    const readObservedLiveWalSnapshot = (): WalSnapshot | null => {
        const internalSharedSnapshot = readInternalSharedWalSnapshot()
        const preferredCycle = lifecycle.state.currentWalSalt0 != null
            ? {
                salt0: lifecycle.state.currentWalSalt0,
                salt1: lifecycle.state.currentWalSalt1 ?? 0,
            }
            : lifecycle.state.lastWalSnapshot
        const selected = selectPreferredLiveWalSnapshot(
            [
                deps.readLiveWalSnapshot?.(),
                deps.tryReadWalSnapshotStableOrNull?.(),
                internalSharedSnapshot,
                syntheticWalSnapshot,
                lifecycle.state.lastWalSnapshot,
            ],
            preferredCycle,
            internalSharedSnapshot,
        )

        ;(lifecycle.state as ControlWorkerRuntimeState & {
            __debugLiveWalSelection?: Record<string, unknown>
        }).__debugLiveWalSelection = {
            preferredCycle,
            authoritativeCycle: internalSharedSnapshot,
            sources: {
                externalLive: deps.readLiveWalSnapshot?.() ?? null,
                externalStable: deps.tryReadWalSnapshotStableOrNull?.() ?? null,
                internalShared: internalSharedSnapshot,
                synthetic: syntheticWalSnapshot,
                lastWalSnapshot: lifecycle.state.lastWalSnapshot,
            },
            selected,
        }

        return cloneWalSnapshot(selected)
    }

    const rememberWalCommitTip = (message: WalCommitSyncMessage): void => {
        if (hasExternalWalSnapshotReader) {
            updateSyntheticWalDebug('wal-commit-tip-skipped', {
                walSalt0: message.walSalt0,
                walSalt1: message.walSalt1,
                frameMax: message.frameMax,
            })
            return
        }

        const frameMax = message.frameMax as number
        const current = syntheticWalSnapshot

        if (
            current == null
            || current.salt0 !== message.walSalt0
            || current.salt1 !== message.walSalt1
        ) {
            syntheticWalSnapshot = {
                salt0: message.walSalt0,
                salt1: message.walSalt1,
                iChange: 0,
                mxFrame: frameMax,
                nBackfill: 0,
            }
            updateSyntheticWalDebug('wal-commit-tip-reset', {
                walSalt0: message.walSalt0,
                walSalt1: message.walSalt1,
                frameMax: message.frameMax,
            })
            return
        }

        if (frameMax > current.mxFrame) {
            syntheticWalSnapshot = {
                ...current,
                mxFrame: frameMax,
                nBackfill: Math.min(current.nBackfill, frameMax),
            }
            updateSyntheticWalDebug('wal-commit-tip-advance', {
                walSalt0: message.walSalt0,
                walSalt1: message.walSalt1,
                frameMax: message.frameMax,
            })
        }
    }

    const rememberCheckpointBackfill = (message: CheckpointDoneMessage): void => {
        if (hasExternalWalSnapshotReader || message.rc !== 0) {
            updateSyntheticWalDebug('checkpoint-backfill-skipped', {
                rc: message.rc,
                logFrames: message.logFrames,
                ckptFrames: message.ckptFrames,
            })
            return
        }

        const internalSharedSnapshot = readInternalSharedWalSnapshot()
        const preferredCycle = lifecycle.state.currentWalSalt0 != null
            ? {
                salt0: lifecycle.state.currentWalSalt0,
                salt1: lifecycle.state.currentWalSalt1 ?? 0,
            }
            : lifecycle.state.lastWalSnapshot
                ?? syntheticWalSnapshot
        const current = selectPreferredLiveWalSnapshot(
            [
                internalSharedSnapshot,
                lifecycle.state.lastWalSnapshot,
                syntheticWalSnapshot,
            ],
            preferredCycle,
            internalSharedSnapshot,
        )
        if (current == null) {
            updateSyntheticWalDebug('checkpoint-backfill-missing-current', {
                rc: message.rc,
                logFrames: message.logFrames,
                ckptFrames: message.ckptFrames,
                preferredCycle,
                internalSharedSnapshot,
            })
            return
        }

        const mxFrame = Math.max(current.mxFrame, message.logFrames, message.ckptFrames)
        syntheticWalSnapshot = {
            ...current,
            mxFrame,
            nBackfill: Math.max(current.nBackfill, Math.min(message.ckptFrames, mxFrame)),
        }
        updateSyntheticWalDebug('checkpoint-backfill-applied', {
            rc: message.rc,
            logFrames: message.logFrames,
            ckptFrames: message.ckptFrames,
            current,
            preferredCycle,
            internalSharedSnapshot,
        })
    }

    const forwardRuntimeMessage = (
        message: Sqlite3MtExternalMessage
    ): void => {
        if (message.type === 'wal-commit-sync') {
            rememberWalCommitTip(message)
        } else if (message.type === 'checkpoint-done') {
            rememberCheckpointBackfill(message)
            ;(lifecycle.state as ControlWorkerRuntimeState & {
                __debugCheckpointDispatch?: Record<string, unknown>
            }).__debugCheckpointDispatch = {
                phase: 'forward-runtime-message',
                seq: message.seq,
                mode: message.mode,
                rc: message.rc,
                logFrames: message.logFrames,
                ckptFrames: message.ckptFrames,
            }
        }

        void lifecycle.runtime.handleMessage(message).catch(error => {
            lifecycle.enterFatalRuntime(error, SQLITE3MT_RUNTIME_DISPATCH_FAILED)
        })
    }

    emitSecureBlobComponentProgress('sqlite3mt-open', 'Creating sqlite3mt control lifecycle', {
        workspace: args.config.workspace,
    })
    lifecycle = createControlWorkerLifecycle({
        durable: deps.durable,
        manifest: deps.manifest,
        rpc: deps.rpc,
        hashPage: deps.hashPage,
        loadWalBytesForBootstrap: deps.loadWalBytesForBootstrap ?? (() => new Uint8Array()),
        primeLiveWalViewForBootstrap: deps.primeLiveWalViewForBootstrap ?? (async () => {}),
        tryReadWalSnapshotStableOrNull:
            deps.tryReadWalSnapshotStableOrNull ?? readObservedBootstrapWalSnapshot,
        postRunCheckpoint: msg => {
            ;(lifecycle.state as ControlWorkerRuntimeState & {
                __debugCheckpointDispatch?: Record<string, unknown>
            }).__debugCheckpointDispatch = {
                phase: 'scheduled',
                seq: msg.seq,
                mode: msg.mode,
                runtimeEpoch: msg.runtimeEpoch.toString(),
            }
            setTimeout(() => {
                try {
                    ;(lifecycle.state as ControlWorkerRuntimeState & {
                        __debugCheckpointDispatch?: Record<string, unknown>
                    }).__debugCheckpointDispatch = {
                        phase: 'worker-run',
                        seq: msg.seq,
                        mode: msg.mode,
                        runtimeEpoch: msg.runtimeEpoch.toString(),
                    }
                    const checkpointDone = handleCheckpointWorkerRun(msg, {
                        runWalCheckpoint: mode => runtimeBridge!.runWalCheckpoint!(mode),
                    })
                    ;(lifecycle.state as ControlWorkerRuntimeState & {
                        __debugCheckpointDispatch?: Record<string, unknown>
                    }).__debugCheckpointDispatch = {
                        phase: 'worker-done',
                        seq: checkpointDone.seq,
                        mode: checkpointDone.mode,
                        rc: checkpointDone.rc,
                        logFrames: checkpointDone.logFrames,
                        ckptFrames: checkpointDone.ckptFrames,
                    }
                    forwardRuntimeMessage(checkpointDone)
                } catch (error) {
                    ;(lifecycle.state as ControlWorkerRuntimeState & {
                        __debugCheckpointDispatch?: Record<string, unknown>
                    }).__debugCheckpointDispatch = {
                        phase: 'worker-error',
                        seq: msg.seq,
                        mode: msg.mode,
                        error: error instanceof Error
                            ? { name: error.name, message: error.message }
                            : String(error),
                    }
                    lifecycle.enterFatalRuntime(error, SQLITE3MT_RUNTIME_DISPATCH_FAILED)
                }
            }, 0)
        },
        kickSyncEngine: reason => {
            requestSyncEngineWithPolicy(reason)
        },
        yieldOneTaskTurnForPendingCommitMessages:
            deps.yieldOneTaskTurnForPendingCommitMessages ?? yieldOneTaskTurn,
        onShutdown: async () => {
            clearLocalBackgroundSyncTimer()
            await deps.onShutdown?.()
        },
        onSnapshotPersistError: deps.onSnapshotPersistError,
        refreshRemoteHeadAfterBootstrap: deps.refreshRemoteHeadAfterBootstrap,
        maybeCompactDbBaseRoot: deps.maybeCompactDbBaseRoot,
        readLiveWalSnapshot: deps.readLiveWalSnapshot ?? readObservedLiveWalSnapshot,
        maxPassiveCheckpointNoProgress: deps.maxPassiveCheckpointNoProgress,
        faultReadAheadPages: deps.faultReadAheadPages,
        nowMs: deps.nowMs,
        onBootstrapProgress: (message, details) => {
            emitSecureBlobComponentProgress('sqlite3mt-bootstrap', message, {
                workspace: args.config.workspace,
                ...(details ?? {}),
            })
        },
        setTimeoutFn: deps.setTimeoutFn,
        sabs: args.sabs,
    })
    lifecycle.state.sdkObservers = deps.observers
    emitSecureBlobComponentProgress('sqlite3mt-open', 'Created sqlite3mt control lifecycle', {
        workspace: args.config.workspace,
    })

    if (deps.kickSyncEngine == null && hasInternalPushDriverDeps(deps)) {
        emitSecureBlobComponentProgress('sqlite3mt-open', 'Building sqlite3mt bridge-owned push driver', {
            workspace: args.config.workspace,
        })
        requestSyncEngine = buildBridgeOwnedPushDriver(lifecycle, deps)
    }

    emitSecureBlobComponentProgress('sqlite3mt-open', 'Initializing sqlite3mt runtime', {
        workspace: args.config.workspace,
        timeoutMs: readSqlite3MtOpenTimeoutMs(),
    })
    await withSqlite3MtOpenTimeout(
        lifecycle.runtime.init(),
        `bootstrap sqlite3mt runtime for ${args.config.workspace}`,
    )
    emitSecureBlobComponentProgress('sqlite3mt-open', 'Initialized sqlite3mt runtime', {
        workspace: args.config.workspace,
        phase: lifecycle.runtime.phase,
    })

    if (lifecycle.runtime.phase !== 'RUNNING') {
        const bootstrapError = lifecycle.state.syncFatalError
        const bootstrapMessage = formatSqlite3MtUnknownError(bootstrapError)
        sdkConsole.error('[sqlite3mt:engine] runtime init failed ' + JSON.stringify({
            workspace: args.config.workspace,
            phase: lifecycle.runtime.phase,
            runtimeEpoch: lifecycle.runtime.runtimeEpoch.toString(),
            bootstrapMessage,
        }))
        throw bootstrapError instanceof Error
            ? bootstrapError
            : new Error(`sqlite3mt runtime bootstrap failed for ${args.config.workspace}: ${bootstrapMessage}`)
    }

    const activeExternalClientRequests = new Map<number, number>()
    let remoteRefreshInFlight: {
        promise: Promise<void>
        startedAt: number
    } | null = null
    const recordExternalClientRequestDebug = (
        event: 'enter' | 'leave' | 'release-attachment',
        connId: number,
        runtimeEpoch: bigint,
    ): void => {
        ;(lifecycle.state as ControlWorkerRuntimeState & {
            __debugExternalClientRequests?: Record<string, unknown>
        }).__debugExternalClientRequests = {
            event,
            connId,
            runtimeEpoch: runtimeEpoch.toString(),
            activeConnIds: Array.from(activeExternalClientRequests.keys()),
            count: lifecycle.state.externalClientRequestCount ?? 0,
        }
    }

    const runtimeBridgeWithDebug: Sqlite3MtRuntimeBridge & {
        __debugRecordExternalClientRequest(
            event: 'enter' | 'leave' | 'release-attachment',
            connId: number,
            runtimeEpoch: bigint,
        ): void
    } = {
        __debugRecordExternalClientRequest: recordExternalClientRequestDebug,
        get runtimeEpoch() {
            return lifecycle.runtime.runtimeEpoch
        },
        databaseAdapter: deps.databaseAdapter
            ?? createSqlite3MtDatabaseAdapter(
                {
                    state: lifecycle.state,
                    hasOutstandingCommitPosts: () => hasAnyOutstandingCommitPosts(args.sabs.sab4),
                    getWalWriteHolders: () => Atomics.load(
                        sab4ScalarView(args.sabs.sab4),
                        Sab4Scalar.WAL_WRITE_HOLDERS,
                    ),
                },
                reason => requestSyncEngineWithPolicy(reason),
                {
                    requestDurableCachePrune: async () => {
                        await lifecycle.requestPersistLocalSnapshot?.()
                    },
                    requestRemoteRefresh: async () => {
                        if (remoteRefreshInFlight != null) {
                            const ageMs = Date.now() - remoteRefreshInFlight.startedAt
                            if (ageMs < REMOTE_REFRESH_INFLIGHT_STALE_AFTER_MS) {
                                await remoteRefreshInFlight.promise
                                return
                            }

                            sdkConsole.warn('[sqlite3mt:engine] dropping stale remote refresh waiter', {
                                workspace: args.config.workspace,
                                ageMs,
                                staleAfterMs: REMOTE_REFRESH_INFLIGHT_STALE_AFTER_MS,
                            })
                        }

                        const refreshTask = (async () => {
                            const reqSeq = lifecycle.state.remoteRefreshReqSeq + 1n
                            lifecycle.state.remoteRefreshReqSeq = reqSeq
                            const { root, version } = await deps.rpc.rpcGetHeadVersioned()
                            applyRemoteHeadIfNewer(
                                lifecycle.state,
                                root,
                                version,
                                reqSeq,
                                {
                                    maybeStartPushOrImport: () => requestSyncEngineWithPolicy('remote-head-update'),
                                    enterFatalRuntime: lifecycle.enterFatalRuntime,
                                }
                            )
                        })()

                        remoteRefreshInFlight = {
                            promise: refreshTask,
                            startedAt: Date.now(),
                        }
                        try {
                            await refreshTask
                        } finally {
                            if (remoteRefreshInFlight?.promise === refreshTask) {
                                remoteRefreshInFlight = null
                            }
                        }
                    },
                },
            ),
        dbName: deps.dbName ?? '',
        hashPage: deps.hashPage,
        createHandleHooks(context) {
            return deps.createHandleHooks?.(context) ?? {}
        },
        postFaultMessage(_conn, msg) {
            forwardRuntimeMessage(msg)
        },
        postCommitSyncMessage(_conn, msg, _transfer) {
            forwardRuntimeMessage(msg)
        },
        enterFatalIoMode(_conn) {
            lifecycle.enterFatalRuntime(SQLITE3MT_CLIENT_IO_FATAL, SQLITE3MT_CLIENT_IO_FATAL)
        },
        handleExternalMessage(message) {
            forwardRuntimeMessage(message)
        },
        enterExternalClientIoFatal(_connId) {
            lifecycle.enterFatalRuntime(SQLITE3MT_CLIENT_IO_FATAL, SQLITE3MT_CLIENT_IO_FATAL)
        },
        enterExternalClientRequest(connId, runtimeEpoch) {
            if (runtimeEpoch !== lifecycle.runtime.runtimeEpoch) {
                return
            }
            const currentDepth = activeExternalClientRequests.get(connId) ?? 0
            activeExternalClientRequests.set(connId, currentDepth + 1)
            if (currentDepth === 0) {
                lifecycle.state.externalClientRequestCount = (lifecycle.state.externalClientRequestCount ?? 0) + 1
            }
            recordExternalClientRequestDebug('enter', connId, runtimeEpoch)
        },
        leaveExternalClientRequest(connId, runtimeEpoch) {
            if (runtimeEpoch !== lifecycle.runtime.runtimeEpoch) {
                return
            }
            const currentDepth = activeExternalClientRequests.get(connId) ?? 0
            if (currentDepth <= 0) {
                return
            }
            if (currentDepth > 1) {
                activeExternalClientRequests.set(connId, currentDepth - 1)
                recordExternalClientRequestDebug('leave', connId, runtimeEpoch)
                return
            }
            activeExternalClientRequests.delete(connId)
            lifecycle.state.externalClientRequestCount = Math.max(
                0,
                (lifecycle.state.externalClientRequestCount ?? 0) - 1,
            )
            recordExternalClientRequestDebug('leave', connId, runtimeEpoch)
            if (lifecycle.state.externalClientRequestCount === 0) {
                requestSyncEngineAfterCheckpointOpportunity('external-client-idle')
            }
        },
        releaseExternalClientAttachment(connId, runtimeEpoch) {
            if (runtimeEpoch !== lifecycle.runtime.runtimeEpoch) {
                return
            }
            const currentDepth = activeExternalClientRequests.get(connId) ?? 0
            if (currentDepth <= 0) {
                recordExternalClientRequestDebug('release-attachment', connId, runtimeEpoch)
                return
            }
            activeExternalClientRequests.delete(connId)
            lifecycle.state.externalClientRequestCount = Math.max(
                0,
                (lifecycle.state.externalClientRequestCount ?? 0) - 1,
            )
            recordExternalClientRequestDebug('release-attachment', connId, runtimeEpoch)
            if (lifecycle.state.externalClientRequestCount === 0) {
                requestSyncEngineAfterCheckpointOpportunity('external-client-idle')
            }
        },
        runWalCheckpoint: deps.runWalCheckpoint ?? (mode => {
            const live = lifecycle.advanceDbHead()
            if (mode === 'TRUNCATE') {
                Atomics.store(sab2SizeView(args.sabs.sab2), 0, 0)
                sab2DataView(args.sabs.sab2).fill(0)
                sab3ShmView(args.sabs.sab3).fill(0)
                const scalar = sab4ScalarView(args.sabs.sab4)
                Atomics.add(scalar, Sab4Scalar.WAL_INDEX_RESET_SEQ, 1)
                Atomics.notify(scalar, Sab4Scalar.WAL_INDEX_RESET_SEQ)
            }
            syntheticWalSnapshot = {
                ...live,
                nBackfill: live.mxFrame,
            }
            return {
                rc: 0,
                logFrames: live.mxFrame,
                ckptFrames: live.mxFrame,
            }
        }),
        async dispose() {
            await lifecycle.runtime.shutdown()
        },
    }

    ;(lifecycle.state as ControlWorkerRuntimeState & {
        __debugSab4ScalarsGetter?: (() => Record<string, number>) | undefined
    }).__debugSab4ScalarsGetter = () => {
        const scalar = sab4ScalarView(args.sabs.sab4)
        return {
            controlReady: Atomics.load(scalar, Sab4Scalar.CONTROL_READY),
            fatalFlag: Atomics.load(scalar, Sab4Scalar.FATAL_FLAG),
            writeGateClosed: Atomics.load(scalar, Sab4Scalar.WRITE_GATE_CLOSED),
            checkpointGateClosed: Atomics.load(scalar, Sab4Scalar.CHECKPOINT_GATE_CLOSED),
            walWriteHolders: Atomics.load(scalar, Sab4Scalar.WAL_WRITE_HOLDERS),
            mainDbReaderCount: Atomics.load(scalar, Sab4Scalar.MAIN_DB_READER_COUNT),
            mainDbWriterConn: Atomics.load(scalar, Sab4Scalar.MAIN_DB_WRITER_CONN),
        }
    }

    runtimeBridge = runtimeBridgeWithDebug

    return runtimeBridge
}

async function createRuntimeContext(
    config: SqliteEngineOpenConfig,
    deps: Sqlite3MtEngineFactoryDeps
): Promise<RuntimeContext> {
    emitSecureBlobComponentProgress('sqlite3mt-open', 'Loading SQLite WASM runtime', {
        workspace: config.workspace,
    })
    const sqlite3 = await withSqlite3MtOpenTimeout(
        deps.loadSqlite3(),
        `load SQLite WASM runtime for ${config.workspace}`,
    )
    emitSecureBlobComponentProgress('sqlite3mt-open', 'Loaded SQLite WASM runtime', {
        workspace: config.workspace,
    })
    emitSecureBlobComponentProgress('sqlite3mt-open', 'Allocating sqlite3mt shared buffers', {
        workspace: config.workspace,
        pageSize: resolveSqlite3MtOpenPageSize(config),
    })
    const sabs = createAndInitializeSabSet({
        pageSize: resolveSqlite3MtOpenPageSize(config),
        initialWalSize: deps.initialWalSize,
        maxWalSize: deps.maxWalSize,
    })
    emitSecureBlobComponentProgress('sqlite3mt-open', 'Creating sqlite3mt runtime bridge', {
        workspace: config.workspace,
    })
    const runtime = await withSqlite3MtOpenTimeout(
        Promise.resolve(deps.createRuntime({ config, sabs })),
        `create runtime bridge for ${config.workspace}`,
    )
    emitSecureBlobComponentProgress('sqlite3mt-open', 'Created sqlite3mt runtime bridge', {
        workspace: config.workspace,
        runtimeEpoch: runtime.runtimeEpoch,
        dbName: runtime.dbName,
    })
    const runtimeId = nextRuntimeId++
    const dbName = runtime.dbName || defaultDbName(config.workspace, runtimeId)

    const context: RuntimeContext = {
        runtimeId,
        sqlite3,
        sabs,
        runtime,
        config,
        dbName,
        activeConnIds: new Set(),
        registrations: new Map(),
        handleCount: 0,
        checkpointHandle: null,
        runtimeOwnerHooks: null,
        disposed: false,
        memoryManager: null as unknown as SqliteMemoryManagerCompat,
    }
    context.memoryManager = createMemoryManagerCompat(context)
    return context
}

function openRegisteredConnection(
    context: RuntimeContext,
    role: Sqlite3MtConnectionRole,
    options: { bootstrapStoragePragmas?: boolean } = {}
): SqliteEngineHandle {
    emitSecureBlobComponentProgress('sqlite3mt-open', 'Opening sqlite3mt registered connection', {
        workspace: context.config.workspace,
        role,
        dbName: context.dbName,
    })
    const connId = allocateConnId(context.activeConnIds)
    const connState = createClientWorkerConnState(connId, context.runtime.runtimeEpoch)
    const bootstrapMode = role === 'runtime-owner'
        ? resolveSqlite3MtBootstrapOpenMode(context.config)
        : null
    const initialDbBootstrapPageCount = bootstrapMode?.kind === 'initial-db'
        ? initialDbPageCount(bootstrapMode.initialDb, context.sabs.pageSize)
        : null
    let registration: ReturnType<typeof registerClientWorkerVfs> | null = null
    let db: Sqlite3Oo1Database | null = null
    let hooks: Sqlite3MtHandleHooks

    try {
        if (
            initialDbBootstrapPageCount != null
            && initialDbBootstrapPageCount > NUM_SLOTS
        ) {
            throw new Error(
                `sqlite3mt openRuntimeOwner bootstrap currently supports initialDb up to ${NUM_SLOTS} pages; got ${initialDbBootstrapPageCount}`
            )
        }

        registration = registerClientWorkerVfs(
            context.sqlite3,
            connState,
            context.sabs.pageSize,
            context.sabs.sab1,
            context.sabs.sab2,
            context.sabs.sab3,
            context.sabs.sab4,
            {
                postFaultMessage: context.runtime.postFaultMessage.bind(context.runtime),
                hashPage: context.runtime.hashPage.bind(context.runtime),
                postCommitSyncMessage: context.runtime.postCommitSyncMessage.bind(context.runtime),
                enterFatalIoMode: context.runtime.enterFatalIoMode.bind(context.runtime),
            },
            `secureblob-mt-${context.runtimeId}-${connId}`
        )
        emitSecureBlobComponentProgress('sqlite3mt-open', 'Registered sqlite3mt VFS connection', {
            workspace: context.config.workspace,
            role,
            connId,
            vfsName: registration.vfsName,
        })

        if (bootstrapMode?.kind === 'seed-pages') {
            primeBootstrapPagesIntoSab(
                context.sabs,
                bootstrapMode.seedPages,
                bootstrapMode.pageCount
            )
        } else if (bootstrapMode?.kind === 'initial-db') {
            primeBootstrapPagesIntoSab(
                context.sabs,
                takeSeedPagesFromInitialDb(bootstrapMode.initialDb, context.sabs.pageSize),
                initialDbBootstrapPageCount!
            )
        }

        sdkConsole.debug('[sqlite3mt:engine] openRegisteredConnection:new-db:start', {
            role,
            dbName: context.dbName,
            vfsName: registration.vfsName,
            pageSize: context.sabs.pageSize,
        })
        emitSecureBlobComponentProgress('sqlite3mt-open', 'Constructing sqlite3mt SQLite DB handle', {
            workspace: context.config.workspace,
            role,
            connId,
            vfsName: registration.vfsName,
        })
        db = new context.sqlite3.oo1.DB({
            filename: buildDbFilename(context.dbName),
            flags: 'ct',
            vfs: registration.vfsName,
        })
        const openedDb = db
        if (openedDb == null) {
            throw new Error('sqlite3mt connection open returned a null database handle')
        }
        sdkConsole.trace('[sqlite3mt:engine] openRegisteredConnection:new-db:ok', {
            role,
            dbPointer: openedDb.pointer,
        })
        emitSecureBlobComponentProgress('sqlite3mt-open', 'Opened sqlite3mt SQLite connection', {
            workspace: context.config.workspace,
            role,
            connId,
            dbPointer: openedDb.pointer,
        })
        sdkConsole.trace('[sqlite3mt:engine] openRegisteredConnection:pragmas:start', {
            role,
            dbPointer: openedDb.pointer,
        })
        applySqlitePragmas({
            role,
            pageSize: context.sabs.pageSize,
            exec: sql => openedDb.exec(sql),
            getPageCount: () => queryPragmaNumber(openedDb, 'page_count'),
            bootstrapStoragePragmas: options.bootstrapStoragePragmas === true,
        })
        if (role !== 'checkpoint') {
            ensureSqliteWalMode({
                role,
                getJournalMode: () => queryPragmaText(openedDb, 'journal_mode'),
                setJournalModeToWal: () => {
                    const row = queryRows<Record<string, unknown>>(openedDb, 'PRAGMA journal_mode = WAL')[0]
                    const value = row?.journal_mode
                    return typeof value === 'string' ? value : null
                },
                bootstrapStoragePragmas: options.bootstrapStoragePragmas === true,
            })
        }
        registration.releaseLeakedShmLocks()
        sdkConsole.trace('[sqlite3mt:engine] openRegisteredConnection:pragmas:ok', {
            role,
            dbPointer: openedDb.pointer,
        })
        emitSecureBlobComponentProgress('sqlite3mt-open', 'Applied sqlite3mt SQLite pragmas', {
            workspace: context.config.workspace,
            role,
            connId,
        })

        const builtinHooks = createBuiltinHandleHooks(context, connState, openedDb)
        const hookOverrides = context.runtime.createHandleHooks({
            role,
            config: context.config,
            connState,
            sabs: context.sabs,
            db: openedDb,
        }) ?? {}
        hooks = {
            ...builtinHooks,
            ...hookOverrides,
        }

        if (bootstrapMode?.kind === 'initial-db') {
            const hasCustomImportFullDb = hooks.importFullDb !== builtinHooks.importFullDb

            if (hasCustomImportFullDb) {
                hooks.importFullDb(bootstrapMode.initialDb)
            }
        }
    } catch (error) {
        try {
            db?.close()
        } catch {
            // Failed opens should not mask the original initialization error.
        }
        registration?.dispose()

        if (!context.disposed && context.handleCount === 0) {
            context.disposed = true
            void context.runtime.dispose()
        }

        emitSecureBlobComponentProgress('sqlite3mt-open', 'Failed opening sqlite3mt registered connection', {
            workspace: context.config.workspace,
            role,
            connId,
            error: error instanceof Error ? error.message : String(error),
        })
        throw error
    }

    if (registration == null || db == null) {
        throw new Error('sqlite3mt connection open did not initialize registration state')
    }

    context.activeConnIds.add(connId)
    context.registrations.set(connId, registration)
    context.handleCount += 1
    if (role === 'runtime-owner') {
        context.runtimeOwnerHooks = hooks
    }

    let closed = false
    const closeHandle = (): void => {
        if (closed) {
            return
        }

        closed = true
        if (role === 'runtime-owner' && context.checkpointHandle != null) {
            const checkpointHandle = context.checkpointHandle
            context.checkpointHandle = null
            context.runtime.runWalCheckpoint = undefined
            try {
                checkpointHandle.close()
            } catch {
                // Closing is best-effort during teardown.
            }
        } else if (role === 'checkpoint') {
            context.checkpointHandle = null
            context.runtime.runWalCheckpoint = undefined
        }

        context.activeConnIds.delete(connId)
        context.registrations.delete(connId)
        context.handleCount = Math.max(0, context.handleCount - 1)
        if (role === 'runtime-owner' && context.runtimeOwnerHooks === hooks) {
            context.runtimeOwnerHooks = null
        }

        try {
            db.close()
        } catch {
            // Closing is best-effort during teardown.
        } finally {
            registration.dispose()
            if (!context.disposed && context.handleCount === 0) {
                context.disposed = true
                void context.runtime.dispose()
            }
        }
    }

    return {
        exec(sql: string, params?: unknown[]) {
            if (params != null && params.length > 0) {
                db.exec({ sql, bind: params })
                return
            }
            db.exec(sql)
        },
        query<T = Record<string, unknown>>(sql: string, params?: unknown[]): T[] {
            return queryRows<T>(db, sql, params)
        },
        getDirtyPages: hooks.getDirtyPages,
        resetDirtyTracking: hooks.resetDirtyTracking,
        exportFullDb: hooks.exportFullDb,
        importFullDb: hooks.importFullDb,
        exportDirtyPages: hooks.exportDirtyPages,
        importMergedPages: hooks.importMergedPages,
        appendWalFramesForSync: hooks.appendWalFramesForSync,
        readPages: hooks.readPages,
        close: closeHandle,
        pageSize: context.sabs.pageSize,
        vfs: {
            get vfsName() {
                return registration.vfsName
            },
            get currentPageSize() {
                return context.sabs.pageSize
            },
        },
        memoryManager: context.memoryManager,
        get dbPtr() {
            return db.pointer
        },
        databaseAdapter: context.runtime.databaseAdapter,
    }
}

function applyRuntimeOwnerBootstrapMode(context: RuntimeContext): void {
    const bootstrapMode = resolveSqlite3MtBootstrapOpenMode(context.config)
    const initialDbBootstrapPageCount = bootstrapMode.kind === 'initial-db'
        ? initialDbPageCount(bootstrapMode.initialDb, context.sabs.pageSize)
        : null

    if (
        initialDbBootstrapPageCount != null
        && initialDbBootstrapPageCount > NUM_SLOTS
    ) {
        throw new Error(
            `sqlite3mt openRuntimeOwner bootstrap currently supports initialDb up to ${NUM_SLOTS} pages; got ${initialDbBootstrapPageCount}`
        )
    }

    if (bootstrapMode.kind === 'seed-pages') {
        primeBootstrapPagesIntoSab(
            context.sabs,
            bootstrapMode.seedPages,
            bootstrapMode.pageCount
        )
    } else if (bootstrapMode.kind === 'initial-db') {
        primeBootstrapPagesIntoSab(
            context.sabs,
            takeSeedPagesFromInitialDb(bootstrapMode.initialDb, context.sabs.pageSize),
            initialDbBootstrapPageCount!
        )
    }
}

function createVirtualRuntimeOwnerHandle(
    context: RuntimeContext,
): SqliteEngineHandle {
    emitSecureBlobComponentProgress('sqlite3mt-open', 'Creating sqlite3mt virtual runtime-owner handle', {
        workspace: context.config.workspace,
        dbName: context.dbName,
    })
    applyRuntimeOwnerBootstrapMode(context)

    let closed = false

    context.handleCount += 1

    const closeHandle = (): void => {
        if (closed) {
            return
        }

        closed = true
        context.handleCount = Math.max(0, context.handleCount - 1)
        if (!context.disposed && context.handleCount === 0) {
            context.disposed = true
            void context.runtime.dispose()
        }
    }

    return {
        exec() {
            throw new Error('sqlite3mt virtual runtime-owner handle does not support direct SQL execution')
        },
        query() {
            throw new Error('sqlite3mt virtual runtime-owner handle does not support direct SQL queries')
        },
        getDirtyPages() {
            return []
        },
        resetDirtyTracking() {},
        exportFullDb() {
            return new Uint8Array(0)
        },
        importFullDb(bytes: Uint8Array) {
            const ctrl = sab1CtrlView(context.sabs.sab1)
            const nPages = Math.ceil(bytes.length / context.sabs.pageSize)
            Atomics.store(ctrl, SAB1_DB_PAGE_COUNT_I32, nPages)

            for (let i = 0; i < nPages; i += 1) {
                const pgno = i + 1
                const offset = i * context.sabs.pageSize
                writeSlotPage(
                    pgno,
                    bytes.subarray(offset, offset + context.sabs.pageSize),
                    context.sabs.sab1,
                    context.sabs.pageSize,
                )
            }
        },
        exportDirtyPages() {
            return new Map()
        },
        importMergedPages(pages: Map<number, Uint8Array>) {
            if (pages.size === 0) {
                return
            }

            const ctrl = sab1CtrlView(context.sabs.sab1)
            let targetPageCount = Atomics.load(ctrl, SAB1_DB_PAGE_COUNT_I32)

            for (const [pgno, pageBytes] of pages) {
                writeSlotPage(pgno, pageBytes, context.sabs.sab1, context.sabs.pageSize)
                if (pgno > targetPageCount) {
                    targetPageCount = pgno
                }
            }

            Atomics.store(ctrl, SAB1_DB_PAGE_COUNT_I32, targetPageCount)
        },
        appendWalFramesForSync(pages: Map<number, Uint8Array>) {
            if (pages.size === 0) {
                return
            }

            const ctrl = sab1CtrlView(context.sabs.sab1)
            let targetPageCount = Atomics.load(ctrl, SAB1_DB_PAGE_COUNT_I32)
            for (const pgno of pages.keys()) {
                if (pgno > targetPageCount) {
                    targetPageCount = pgno
                }
            }

            const views = createWalWriterViews(context.sabs)
            const currentPage1 = pages.get(1) ?? null
            const appendResult = appendWalFrames(
                views,
                pages,
                targetPageCount,
                currentPage1
            )

            const orderedPgnos = Array.from(pages.keys()).sort((left, right) => left - right)
            const newFrames = orderedPgnos.map((pgno, index) => ({
                pgno,
                frameIdx: appendResult.newMxFrame - orderedPgnos.length + index + 1,
            }))
            updateWalIndex(views, newFrames)

            const walHeader = parseWalHeader(views.walDataView.subarray(0, WAL_HDR_SIZE))
            if (walHeader != null) {
                updateWalIndexHdr(
                    views,
                    appendResult.newMxFrame,
                    targetPageCount,
                    appendResult.finalCksum,
                    walHeader
                )
            }

            for (const registration of context.registrations.values()) {
                registration.pullMappedShmRegionsFromShared()
            }
        },
        async readPages() {
            return new Map()
        },
        close: closeHandle,
        pageSize: context.sabs.pageSize,
        vfs: {
            get vfsName() {
                return `secureblob-mt-${context.runtimeId}-virtual-runtime-owner`
            },
            get currentPageSize() {
                return context.sabs.pageSize
            },
        },
        memoryManager: context.memoryManager,
        get dbPtr() {
            return 0
        },
        databaseAdapter: context.runtime.databaseAdapter,
    }
}

export function createSqlite3MtEngineFactory(
    deps: Sqlite3MtEngineFactoryDeps
): SqliteEngineFactory {
    let runtimeContext: RuntimeContext | null = null

    const factory: Sqlite3MtAttachmentCapableFactory = {
        async openRuntimeOwner(config) {
            if (runtimeContext != null && !runtimeContext.disposed) {
                throw new Error('sqlite3mt engine factory already has an active runtime-owner handle')
            }

            emitSecureBlobComponentProgress('sqlite3mt-open', 'Opening sqlite3mt runtime owner', {
                workspace: config.workspace,
                virtualRuntimeOwner: deps.virtualRuntimeOwner === true,
            })
            runtimeContext = await createRuntimeContext(config, deps)
            let runtimeOwner: SqliteEngineHandle | null = null

            try {
                if (deps.virtualRuntimeOwner) {
                    runtimeOwner = createVirtualRuntimeOwnerHandle(runtimeContext)
                    emitSecureBlobComponentProgress('sqlite3mt-open', 'Opened sqlite3mt virtual runtime owner', {
                        workspace: config.workspace,
                    })
                    return runtimeOwner
                }

                runtimeOwner = openRegisteredConnection(runtimeContext, 'runtime-owner')
                const checkpointHandle = openRegisteredConnection(runtimeContext, 'checkpoint')
                runtimeContext.checkpointHandle = checkpointHandle
                runtimeContext.runtime.runWalCheckpoint = mode => runWalCheckpointOnHandle(
                    runtimeContext!.sqlite3,
                    checkpointHandle,
                    mode
                )
                emitSecureBlobComponentProgress('sqlite3mt-open', 'Opened sqlite3mt runtime owner', {
                    workspace: config.workspace,
                })
                return runtimeOwner
            } catch (error) {
                runtimeContext.runtime.runWalCheckpoint = undefined
                runtimeContext.checkpointHandle?.close()
                runtimeContext.checkpointHandle = null
                runtimeOwner?.close()
                throw error
            }
        },

        async openClient(_runtimeOwner) {
            if (runtimeContext == null || runtimeContext.disposed) {
                throw new Error('sqlite3mt runtime owner must be opened before client handles')
            }

            emitSecureBlobComponentProgress('sqlite3mt-open', 'Opening sqlite3mt client handle', {
                workspace: runtimeContext.config.workspace,
            })
            const client = openRegisteredConnection(runtimeContext, 'client', {
                bootstrapStoragePragmas: deps.virtualRuntimeOwner === true,
            })
            emitSecureBlobComponentProgress('sqlite3mt-open', 'Opened sqlite3mt client handle', {
                workspace: runtimeContext.config.workspace,
            })
            return client
        },

        reserveClientAttachment() {
            if (runtimeContext == null || runtimeContext.disposed) {
                throw new Error('sqlite3mt runtime owner must be opened before reserving client attachments')
            }

            const connId = allocateConnId(runtimeContext.activeConnIds)
            runtimeContext.activeConnIds.add(connId)

            return {
                connId,
                runtimeEpoch: runtimeContext.runtime.runtimeEpoch,
                dbName: runtimeContext.dbName,
                pageSize: runtimeContext.sabs.pageSize,
                bootstrapStoragePragmas: deps.virtualRuntimeOwner === true,
                logging: deps.logging,
                sab1: runtimeContext.sabs.sab1,
                sab2: runtimeContext.sabs.sab2,
                sab3: runtimeContext.sabs.sab3,
                sab4: runtimeContext.sabs.sab4,
            }
        },

        releaseClientAttachment(connId) {
            const context = runtimeContext
            if (context?.runtime.releaseExternalClientAttachment != null) {
                context.runtime.releaseExternalClientAttachment(
                    connId,
                    context.runtime.runtimeEpoch,
                )
            } else {
                context?.runtime.leaveExternalClientRequest?.(
                    connId,
                    context.runtime.runtimeEpoch,
                )
            }
            ;(context?.runtime as Sqlite3MtRuntimeBridge & {
                __debugRecordExternalClientRequest?: (
                    event: 'enter' | 'leave' | 'release-attachment',
                    connId: number,
                    runtimeEpoch: bigint,
                ) => void
            } | undefined)?.__debugRecordExternalClientRequest?.(
                'release-attachment',
                connId,
                context?.runtime.runtimeEpoch ?? 0n,
            )
            context?.activeConnIds.delete(connId)
        },

        dispatchExternalMessage(message) {
            if (runtimeContext == null || runtimeContext.disposed) {
                return
            }

            runtimeContext.runtime.handleExternalMessage?.(message)
        },

        enterExternalClientIoFatal(connId) {
            if (runtimeContext == null || runtimeContext.disposed) {
                return
            }

            runtimeContext.runtime.enterExternalClientIoFatal?.(connId)
        },

        enterExternalClientRequest(connId, runtimeEpoch) {
            if (runtimeContext == null || runtimeContext.disposed) {
                return
            }

            runtimeContext.runtime.enterExternalClientRequest?.(connId, runtimeEpoch)
        },

        leaveExternalClientRequest(connId, runtimeEpoch) {
            if (runtimeContext == null || runtimeContext.disposed) {
                return
            }

            runtimeContext.runtime.leaveExternalClientRequest?.(connId, runtimeEpoch)
        },
    }

    return factory
}

function asSqlite3MtAttachmentCapableFactory(
    factory: SqliteEngineFactory
): Sqlite3MtAttachmentCapableFactory {
    const candidate = factory as Partial<Sqlite3MtAttachmentCapableFactory>
    if (
        typeof candidate.reserveClientAttachment !== 'function'
        || typeof candidate.releaseClientAttachment !== 'function'
        || typeof candidate.dispatchExternalMessage !== 'function'
        || typeof candidate.enterExternalClientIoFatal !== 'function'
        || typeof candidate.enterExternalClientRequest !== 'function'
        || typeof candidate.leaveExternalClientRequest !== 'function'
    ) {
        throw new Error('sqlite3mt engine factory does not support external client attachments')
    }

    return candidate as Sqlite3MtAttachmentCapableFactory
}

export function reserveSqlite3MtClientAttachment(
    factory: SqliteEngineFactory
): Sqlite3MtClientAttachment {
    return asSqlite3MtAttachmentCapableFactory(factory).reserveClientAttachment()
}

export function releaseSqlite3MtClientAttachment(
    factory: SqliteEngineFactory,
    connId: number
): void {
    asSqlite3MtAttachmentCapableFactory(factory).releaseClientAttachment(connId)
}

export function dispatchSqlite3MtExternalMessage(
    factory: SqliteEngineFactory,
    message: Sqlite3MtExternalMessage
): void {
    asSqlite3MtAttachmentCapableFactory(factory).dispatchExternalMessage(message)
}

export function enterSqlite3MtExternalClientIoFatal(
    factory: SqliteEngineFactory,
    connId: number
): void {
    asSqlite3MtAttachmentCapableFactory(factory).enterExternalClientIoFatal(connId)
}

export function enterSqlite3MtExternalClientRequest(
    factory: SqliteEngineFactory,
    connId: number,
    runtimeEpoch: bigint,
): void {
    asSqlite3MtAttachmentCapableFactory(factory).enterExternalClientRequest(connId, runtimeEpoch)
}

export function leaveSqlite3MtExternalClientRequest(
    factory: SqliteEngineFactory,
    connId: number,
    runtimeEpoch: bigint,
): void {
    asSqlite3MtAttachmentCapableFactory(factory).leaveExternalClientRequest(connId, runtimeEpoch)
}
